import React from 'react'
import { api, canonicalStoredFileIdForApiPath, getApiErrorMessage } from '../lib/api'
import { asArray } from '../lib/collections'
import MenuGuideButton from '../components/MenuGuideButton'

const AUTO_AUTH_POLL_INTERVAL_MS = 5000
const AUTO_AUTH_POLL_MAX_ATTEMPTS = 54

const EXTERNAL_API_SCENARIOS = {
  incomeTaxCredit: {
    label: 'CODEF 연말정산간소화',
    badge: 'API',
    description: '국세청 홈택스 소득세액공제자료. 최근 3개 귀속년도를 같은 다건 요청 ID로 자동 조회합니다.',
    initialPath: '/ops/api-tests/external/codef/income-tax-credit/initial',
    twoWayPath: '/ops/api-tests/external/codef/income-tax-credit/twoway',
    multiYear: true,
    readable: 'incomeTaxCredit',
  },
  hiraMedical: {
    label: 'CODEF 내진료정보열람',
    badge: 'API',
    description: 'HIRA 내진료정보열람 상품을 테스트합니다. 기간 기준 단건 조회 후 추가 인증을 자동 확인합니다.',
    initialPath: '/ops/api-tests/external/codef/hira/medical/initial',
    twoWayPath: '/ops/api-tests/external/codef/hira/medical/twoway',
    multiYear: false,
    readable: 'generic',
  },
  hiraCarInsurance: {
    label: 'CODEF 자동차보험 진료내역',
    badge: 'API',
    description: 'HIRA 자동차보험 진료내역 상품을 테스트합니다. 기간 기준 단건 조회 후 추가 인증을 자동 확인합니다.',
    initialPath: '/ops/api-tests/external/codef/hira/car-insurance/initial',
    twoWayPath: '/ops/api-tests/external/codef/hira/car-insurance/twoway',
    multiYear: false,
    readable: 'generic',
  },
}

function latestThreeTaxYears() {
  const latest = new Date().getFullYear() - 1
  return [latest, latest - 1, latest - 2].map(String)
}

function defaultDateRange() {
  const end = new Date()
  const start = new Date()
  start.setFullYear(start.getFullYear() - 3)
  const ymd = d => `${d.getFullYear()}${String(d.getMonth() + 1).padStart(2, '0')}${String(d.getDate()).padStart(2, '0')}`
  return { startDate: ymd(start), endDate: ymd(end) }
}

function makeSessionId() {
  const rand = typeof crypto !== 'undefined' && crypto?.randomUUID
    ? crypto.randomUUID()
    : `${Date.now()}-${Math.random().toString(16).slice(2)}`
  return `sgez-tax-${rand}`
}

function extractTwoWayInfo(result) {
  const data = result?.parsedBody?.data
  if (!data || typeof data !== 'object') return null
  return {
    jobIndex: data.jobIndex,
    threadIndex: data.threadIndex,
    jti: data.jti,
    twoWayTimestamp: data.twoWayTimestamp,
    method: data.method,
    ...(data.extraInfo ? { extraInfo: data.extraInfo } : {}),
  }
}

function pretty(value) {
  if (value == null || value === '') return ''
  if (typeof value === 'string') {
    try {
      return JSON.stringify(JSON.parse(value), null, 2)
    } catch {
      return value
    }
  }
  try {
    return JSON.stringify(value, null, 2)
  } catch {
    return String(value)
  }
}

function codeOf(result) {
  return result?.parsedBody?.result?.code || ''
}

function needsTwoWay(result) {
  return codeOf(result) === 'CF-03002' && result?.parsedBody?.data?.continue2Way === true
}

function formatWon(value) {
  const n = Number(String(value ?? '').replace(/[^0-9.-]/g, ''))
  if (!Number.isFinite(n)) return value ? `${value}원` : '-'
  return `${n.toLocaleString()}원`
}

function formatDate(value) {
  const s = String(value || '').replace(/[^0-9]/g, '')
  if (s.length !== 8) return value || '-'
  return `${s.slice(0, 4)}-${s.slice(4, 6)}-${s.slice(6, 8)}`
}

function deductibleLabel(code) {
  if (code === '2') return '보험료'
  if (code === '3') return '의료비'
  return `공제항목 ${code || '-'}`
}

function summarizeIncomeTaxCredit(result) {
  const body = result?.parsedBody
  const data = Array.isArray(body?.data) ? body.data : []
  const sections = data.map(section => {
    const item = String(section?.resDeductibleItem || '')
    const rows = Array.isArray(section?.resBasicList) ? section.resBasicList : []
    return { item, label: deductibleLabel(item), rows }
  })
  const insurancePremiums = sections.find(s => s.item === '2')?.rows || []
  const medicalRows = sections.find(s => s.item === '3')?.rows || []
  const medicalExpenses = medicalRows.filter(r => String(r?.resType || '') === '0')
  const insuranceBenefits = medicalRows.filter(r => String(r?.resType || '') === '1')

  const detailAmountSum = rows => rows.reduce((sum, row) => {
    const details = Array.isArray(row?.resDetailList) ? row.resDetailList : []
    const value = details.reduce((s, d) => s + (Number(String(d?.resAmount || '').replace(/[^0-9.-]/g, '')) || 0), 0)
    return sum + value
  }, 0)

  return {
    code: body?.result?.code || '',
    message: body?.result?.message || '',
    transactionId: body?.result?.transactionId || '',
    sections,
    insurancePremiums,
    medicalExpenses,
    insuranceBenefits,
    totals: {
      insurancePremium: insurancePremiums.reduce((s, r) => s + (Number(r?.resAmountPayment) || 0), 0),
      medicalExpense: detailAmountSum(medicalExpenses),
      insuranceBenefit: detailAmountSum(insuranceBenefits),
    },
  }
}

function sleep(ms) {
  return new Promise(resolve => setTimeout(resolve, ms))
}

function ReadableResultView({ result, year, scenario }) {
  if (!result) {
    return <div className="rounded bg-slate-50 px-3 py-8 text-center text-sm text-slate-500">아직 호출 결과가 없습니다.</div>
  }
  if (scenario?.readable !== 'incomeTaxCredit') {
    const body = result?.parsedBody
    const code = body?.result?.code || ''
    const message = body?.result?.message || ''
    const txId = body?.result?.transactionId || ''
    const data = body?.data
    const count = Array.isArray(data) ? data.length : (data && typeof data === 'object' ? Object.keys(data).length : 0)
    return (
      <div className="max-h-[520px] overflow-auto rounded border border-slate-200 bg-slate-50 p-3">
        <div className="rounded-lg bg-white p-3">
          <div className="flex flex-wrap items-center justify-between gap-2">
            <div>
              <div className="text-sm font-semibold text-slate-900">{scenario?.label || '외부 API'} 결과</div>
              <div className="mt-0.5 text-xs text-slate-500">code {code || '-'} · {message || '-'}</div>
            </div>
            {txId && <div className="font-mono text-[11px] text-slate-400">{txId}</div>}
          </div>
        </div>
        <div className="mt-3 grid gap-2 sm:grid-cols-3">
          <div className="rounded-lg bg-white p-3">
            <div className="text-xs text-slate-500">상품</div>
            <div className="mt-1 text-sm font-semibold text-slate-900">{scenario?.label || '-'}</div>
          </div>
          <div className="rounded-lg bg-white p-3">
            <div className="text-xs text-slate-500">데이터 형태</div>
            <div className="mt-1 text-sm font-semibold text-slate-900">{Array.isArray(data) ? 'Array' : (data && typeof data === 'object' ? 'Object' : '-')}</div>
          </div>
          <div className="rounded-lg bg-white p-3">
            <div className="text-xs text-slate-500">항목 수</div>
            <div className="mt-1 text-sm font-semibold text-slate-900">{count}</div>
          </div>
        </div>
        <div className="mt-3 rounded-lg bg-white p-3 text-sm text-slate-600">
          이 API의 전용 결과 레이아웃은 아직 정의되지 않았습니다. 원문 JSON과 복사 기능은 동일하게 사용할 수 있습니다.
        </div>
      </div>
    )
  }
  const summary = summarizeIncomeTaxCredit(result)
  const showData = summary.code === 'CF-00000'
  return (
    <div className="max-h-[520px] overflow-auto rounded border border-slate-200 bg-slate-50 p-3">
      <div className="rounded-lg bg-white p-3">
        <div className="flex flex-wrap items-center justify-between gap-2">
          <div>
            <div className="text-sm font-semibold text-slate-900">{year}년 소득세액공제자료</div>
            <div className="mt-0.5 text-xs text-slate-500">code {summary.code || '-'} · {summary.message || '-'}</div>
          </div>
          {summary.transactionId && <div className="font-mono text-[11px] text-slate-400">{summary.transactionId}</div>}
        </div>
      </div>

      {!showData ? (
        <div className="mt-3 rounded-lg border border-amber-100 bg-amber-50 px-3 py-3 text-sm text-amber-800">
          성공 응답(`CF-00000`)이 아니어서 상세 데이터 요약을 표시하지 않습니다. 원문 JSON을 확인해 주세요.
        </div>
      ) : (
        <>
          <div className="mt-3 grid gap-2 sm:grid-cols-3">
            <div className="rounded-lg bg-white p-3">
              <div className="text-xs text-slate-500">보험료</div>
              <div className="mt-1 text-lg font-semibold text-slate-900">{formatWon(summary.totals.insurancePremium)}</div>
              <div className="mt-0.5 text-xs text-slate-500">{summary.insurancePremiums.length}건</div>
            </div>
            <div className="rounded-lg bg-white p-3">
              <div className="text-xs text-slate-500">의료비</div>
              <div className="mt-1 text-lg font-semibold text-slate-900">{formatWon(summary.totals.medicalExpense)}</div>
              <div className="mt-0.5 text-xs text-slate-500">{summary.medicalExpenses.length}개 기관</div>
            </div>
            <div className="rounded-lg bg-white p-3">
              <div className="text-xs text-slate-500">실손 수령</div>
              <div className="mt-1 text-lg font-semibold text-slate-900">{formatWon(summary.totals.insuranceBenefit)}</div>
              <div className="mt-0.5 text-xs text-slate-500">{summary.insuranceBenefits.length}건</div>
            </div>
          </div>

          <div className="mt-3 rounded-lg bg-white p-3">
            <div className="mb-2 text-sm font-semibold text-slate-900">의료비 상세</div>
            <div className="space-y-2">
              {summary.medicalExpenses.length === 0 ? (
                <div className="py-4 text-center text-sm text-slate-500">의료비 내역이 없습니다.</div>
              ) : summary.medicalExpenses.map((row, idx) => (
                <div key={`${row.resCompanyIdentityNo || row.resCompanyNm}-${idx}`} className="rounded border border-slate-100 px-3 py-2">
                  <div className="flex flex-wrap items-start justify-between gap-2">
                    <div>
                      <div className="text-sm font-medium text-slate-900">{row.resCompanyNm || '-'}</div>
                      <div className="mt-0.5 text-xs text-slate-500">{row.resUserNm || '-'} · {row.resInsureType || '-'}</div>
                    </div>
                    <div className="text-sm font-semibold text-slate-900">{formatWon(row.resAmountPayment)}</div>
                  </div>
                  <div className="mt-2 flex flex-wrap gap-1.5">
                    {(Array.isArray(row.resDetailList) ? row.resDetailList : []).map((d, dIdx) => (
                      <span key={`${d.resDatePayment}-${dIdx}`} className="rounded-full bg-slate-100 px-2 py-1 text-[11px] text-slate-700">
                        {formatDate(d.resDatePayment)} · {formatWon(d.resAmount)}
                      </span>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          </div>

          <div className="mt-3 rounded-lg bg-white p-3">
            <div className="mb-2 text-sm font-semibold text-slate-900">실손의료보험금 수령</div>
            <div className="space-y-2">
              {summary.insuranceBenefits.length === 0 ? (
                <div className="py-4 text-center text-sm text-slate-500">실손 수령 내역이 없습니다.</div>
              ) : summary.insuranceBenefits.map((row, idx) => (
                <div key={`${row.resAccount || row.resCompanyNm}-${idx}`} className="rounded border border-slate-100 px-3 py-2">
                  <div className="flex flex-wrap items-start justify-between gap-2">
                    <div>
                      <div className="text-sm font-medium text-slate-900">{row.resCompanyNm || '-'}</div>
                      <div className="mt-0.5 text-xs text-slate-500">{row.resInsureType || '-'} · {row.resAccount || '-'}</div>
                    </div>
                    <div className="text-sm font-semibold text-slate-900">{formatWon(row.resAmountPayment)}</div>
                  </div>
                  <div className="mt-2 flex flex-wrap gap-1.5">
                    {(Array.isArray(row.resDetailList) ? row.resDetailList : []).filter(d => Number(d?.resAmount) > 0).map((d, dIdx) => (
                      <span key={`${d.resMonth}-${dIdx}`} className="rounded-full bg-blue-50 px-2 py-1 text-[11px] text-blue-700">
                        {d.resMonth || '-'}월 · {formatWon(d.resAmount)}
                      </span>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          </div>

          <div className="mt-3 rounded-lg bg-white p-3">
            <div className="mb-2 text-sm font-semibold text-slate-900">보험료 자료</div>
            <div className="space-y-2">
              {summary.insurancePremiums.length === 0 ? (
                <div className="py-4 text-center text-sm text-slate-500">보험료 내역이 없습니다.</div>
              ) : summary.insurancePremiums.map((row, idx) => (
                <div key={`${row.resAccount || row.resCompanyNm}-${idx}`} className="rounded border border-slate-100 px-3 py-2">
                  <div className="flex flex-wrap items-start justify-between gap-2">
                    <div>
                      <div className="text-sm font-medium text-slate-900">{row.resCompanyNm || '-'}</div>
                      <div className="mt-0.5 text-xs text-slate-500">{row.resInsureType || '-'} · 증권/계좌 {row.resAccount || '-'}</div>
                    </div>
                    <div className="text-sm font-semibold text-slate-900">{formatWon(row.resAmountPayment)}</div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </>
      )}
    </div>
  )
}

function MultiYearReadableResultView({ years, yearResults, activeYear, onSelectYear, scenario }) {
  const selectedYear = years.includes(activeYear) ? activeYear : years[0]
  const selectedResult = yearResults[selectedYear]?.result || null
  return (
    <div className="space-y-3">
      <div className="flex flex-wrap gap-2">
        {years.map(year => {
          const row = yearResults[year]
          const code = row?.code || '대기'
          return (
            <button
              key={year}
              type="button"
              className={`rounded border px-3 py-2 text-left text-xs ${
                selectedYear === year ? 'border-brand-navy bg-sky-50 text-brand-navy ring-1 ring-brand-navy/10' : 'border-slate-200 bg-white text-slate-700 hover:bg-slate-50'
              }`}
              onClick={() => onSelectYear(year)}
            >
              <div className="font-semibold">{year}년</div>
              <div className={`mt-0.5 font-mono ${selectedYear === year ? 'text-white/80' : 'text-slate-500'}`}>{code}</div>
            </button>
          )
        })}
      </div>
      <ReadableResultView result={selectedResult} year={selectedYear} scenario={scenario} />
    </div>
  )
}

function formatBytes(value) {
  const n = Number(value || 0)
  if (!Number.isFinite(n) || n <= 0) return '-'
  if (n < 1024 * 1024) return `${Math.round(n / 1024).toLocaleString()}KB`
  return `${(n / (1024 * 1024)).toFixed(1)}MB`
}

const CLAIM_TEST_MODE_OPTIONS = [
  { value: 'PDF_ONLY', label: 'PDF 생성' },
  { value: 'TIF_ONLY', label: 'TIF 생성' },
  { value: 'SEND_PDF_FAX', label: 'PDF 발송' },
  { value: 'SEND_FAX', label: 'TIF 발송' },
]
const CLAIM_TEST_SEND_MODES = new Set(['SEND_PDF_FAX', 'SEND_FAX'])
const DEFAULT_CLAIM_TEST_FAX_NUMBER = '070-7614-8200'

function modeLabel(mode) {
  if (mode === 'SEND_PDF_FAX') return '테스트 팩스(PDF) 발송'
  if (mode === 'SEND_FAX') return '테스트 팩스(TIF) 발송'
  if (mode === 'TIF_ONLY') return 'TIF만 생성'
  return 'PDF만 생성'
}

function isClaimTestSendMode(mode) {
  return CLAIM_TEST_SEND_MODES.has(mode)
}

function claimTestFileKind(row) {
  const kind = String(row?.fileKind || '').toUpperCase()
  if (kind.includes('TIF')) return 'TIF'
  if (kind.includes('PDF')) return 'PDF'
  const filename = String(row?.filename || '').toLowerCase()
  if (filename.endsWith('.tif') || filename.endsWith('.tiff')) return 'TIF'
  return 'PDF'
}

function claimTestFallbackFilename(row) {
  const ext = claimTestFileKind(row).toLowerCase()
  return `${row?.insurerName || '보험사'}_보험금청구_운영테스트.${ext}`
}

function isClaimTestInsurerSelectable(insurer) {
  return !!insurer && insurer.claimEnabled !== false && insurer.templatePdfReady !== false
}

function claimTestAverageBytesPerPage(row) {
  const fromApi = Number(row?.averageBytesPerPage || 0)
  if (Number.isFinite(fromApi) && fromApi > 0) return fromApi
  const bytes = Number(row?.fileByteSize || row?.pdfByteSize || 0)
  const pages = Number(row?.generatedPageCount || 0)
  return bytes > 0 && pages > 0 ? Math.round(bytes / pages) : 0
}

function statusLabel(status) {
  if (status === 'SUBMITTED') return '전송 요청'
  if (status === 'DELIVERED') return '전송 완료'
  if (status === 'FAILED') return '전송 실패'
  if (status === 'CANCELLED') return '취소'
  if (status === 'PENDING') return '대기'
  return status || '-'
}

function groupFieldDrafts(fields, filterText) {
  const q = String(filterText || '').trim().toLowerCase()
  const groups = []
  const byGroup = new Map()
  ;(fields || []).forEach(field => {
    if (!field?.key) return
    if (q) {
      const haystack = `${field.label || ''} ${field.key || ''} ${field.group || ''}`.toLowerCase()
      if (!haystack.includes(q)) return
    }
    const group = field.group || '기타'
    if (!byGroup.has(group)) {
      const row = { group, fields: [] }
      byGroup.set(group, row)
      groups.push(row)
    }
    byGroup.get(group).fields.push(field)
  })
  return groups
}

function downloadBlob(blob, filename) {
  const url = URL.createObjectURL(blob)
  const a = document.createElement('a')
  a.href = url
  a.download = filename || '보험금청구_운영테스트.pdf'
  document.body.appendChild(a)
  a.click()
  document.body.removeChild(a)
  window.setTimeout(() => URL.revokeObjectURL(url), 1000)
}

function todayIsoDate() {
  const d = new Date()
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`
}

function coverageOperationExcelFilename(isSummary) {
  return `운영테스트_${isSummary ? '보장요약리포트' : '보유계약목록'}_${todayIsoDate()}.xlsx`
}

function ClaimTestFieldInput({ field, value, onChange }) {
  const type = String(field?.inputType || 'TEXT').toUpperCase()
  const options = Array.isArray(field?.options) ? field.options : []
  const commonClass = 'mt-1 w-full rounded border border-slate-200 px-3 py-2 text-sm focus:border-brand-navy focus:outline-none focus:ring-1 focus:ring-brand-navy/20'
  if (options.length > 0) {
    return (
      <select className={commonClass} value={value || ''} onChange={e => onChange(e.target.value)}>
        <option value="">선택</option>
        {options.map(option => (
          <option key={`${field.key}-${option.value}`} value={option.value}>{option.label || option.value}</option>
        ))}
      </select>
    )
  }
  if (type === 'CHECKBOX') {
    return (
      <select className={commonClass} value={value || ''} onChange={e => onChange(e.target.value)}>
        <option value="">선택</option>
        <option value="Y">예</option>
        <option value="N">아니오</option>
      </select>
    )
  }
  if (type === 'TEXTAREA') {
    return <textarea className={`${commonClass} min-h-[72px]`} value={value || ''} onChange={e => onChange(e.target.value)} />
  }
  const inputType = type === 'NUMBER' ? 'number' : (type === 'DATE' ? 'date' : 'text')
  return <input className={commonClass} type={inputType} value={value || ''} onChange={e => onChange(e.target.value)} />
}



function CoverageOperationTestPanel({ mode = 'analysis' }) {
  const isSummary = mode === 'summary'
  const [file, setFile] = React.useState(null)
  const [loading, setLoading] = React.useState(false)
  const [downloading, setDownloading] = React.useState(false)
  const [error, setError] = React.useState('')
  const [result, setResult] = React.useState(null)
  const title = isSummary ? '보장요약 테스트' : '보장분석 테스트'
  const endpoint = isSummary ? '/ops/operation-tests/coverage/summary' : '/ops/operation-tests/coverage/analysis'
  const excelEndpoint = `${endpoint}/excel`
  const draft = result?.draft || {}
  const diagnostics = result?.diagnostics || {}
  const overview = diagnostics?.overview || {}
  const contracts = Array.isArray(draft.contracts) ? draft.contracts : []
  const reasons = Array.isArray(diagnostics.reasons) ? diagnostics.reasons : []
  const summaryRows = Array.isArray(result?.summaryReport?.rows) ? result.summaryReport.rows : []

  const runTest = async () => {
    setError('')
    setResult(null)
    if (!file) {
      setError('테스트할 보장분석 파일을 선택해 주세요.')
      return
    }
    setLoading(true)
    try {
      const formData = new FormData()
      formData.append('file', file)
      const res = await api.post(endpoint, formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
        __silent: true,
      })
      setResult(res?.data?.data || res?.data || null)
    } catch (e) {
      setError(getApiErrorMessage(e, `${title} 실행에 실패했습니다.`))
    } finally {
      setLoading(false)
    }
  }

  const downloadExcel = async () => {
    setError('')
    if (!result) {
      setError('먼저 테스트를 실행한 뒤 다운로드해 주세요.')
      return
    }
    setDownloading(true)
    try {
      const res = await api.post(excelEndpoint, {
        draft: result.draft || null,
        summaryReport: result.summaryReport || null,
      }, {
        responseType: 'blob',
        __silent: true,
      })
      downloadBlob(res.data, coverageOperationExcelFilename(isSummary))
    } catch (e) {
      setError(getApiErrorMessage(e, '엑셀 파일을 다운로드하지 못했습니다.'))
    } finally {
      setDownloading(false)
    }
  }

  return (
    <div className="space-y-5">
      <section className="rounded-lg border border-slate-200 bg-white p-5 shadow-sm">
        <div className="flex flex-col gap-4 lg:flex-row lg:items-end lg:justify-between">
          <div>
            <h2 className="text-base font-semibold text-slate-900">{title}</h2>
            <div className="mt-3 flex flex-wrap items-center gap-2">
              <label className="cursor-pointer rounded border border-slate-300 bg-white px-3 py-2 text-sm font-semibold text-slate-700 hover:bg-slate-50">
                파일 선택
                <input type="file" className="hidden" accept="application/pdf,text/plain,image/jpeg,image/png,.pdf,.txt,.jpg,.jpeg,.png" onChange={e => { setFile(e.target.files?.[0] || null); setResult(null); setError('') }} />
              </label>
              <button type="button" className="rounded bg-brand-navy px-4 py-2 text-sm font-semibold text-white hover:opacity-95 disabled:opacity-50" disabled={loading || !file} onClick={runTest}>
                {loading ? '분석 중...' : '테스트 실행'}
              </button>
              <button type="button" className="rounded border border-slate-300 bg-white px-3 py-2 text-sm font-semibold text-slate-700 hover:bg-slate-50 disabled:opacity-50" disabled={loading || downloading || !result} onClick={downloadExcel}>
                {downloading ? '다운로드 중...' : '엑셀 다운로드'}
              </button>
            </div>
            {file && <div className="mt-2 text-xs text-slate-500">{file.name} ? {formatBytes(file.size)}</div>}
          </div>
          {result && (
            <div className="grid min-w-[320px] grid-cols-4 gap-2 text-center">
              <CoverageStat label="계약" value={overview.contractCount ?? contracts.length} />
              <CoverageStat label="담보" value={overview.coverageCount ?? '-'} />
              <CoverageStat label="요약" value={overview.summaryRowCount ?? '-'} />
              <CoverageStat label="경고" value={overview.warningCount ?? 0} />
            </div>
          )}
        </div>
        {error && <div className="mt-4 rounded border border-rose-200 bg-rose-50 px-3 py-2 text-sm text-rose-700">{error}</div>}
      </section>

      {result && (
        <>
          <section className="rounded-lg border border-slate-200 bg-white p-5 shadow-sm">
            <div className="flex flex-wrap items-center justify-between gap-2">
              <h2 className="text-base font-semibold text-slate-900">차이 원인 진단</h2>
              <span className="text-xs text-slate-500">parser {result.parserVersion || '-'}</span>
            </div>
            <div className="mt-3 grid gap-3 lg:grid-cols-[minmax(0,1fr)_340px]">
              <div className="space-y-2">
                {reasons.map((reason, idx) => (
                  <div key={`${reason.title}-${idx}`} className={`rounded border px-3 py-2 text-sm ${coverageReasonClass(reason.level)}`}>
                    <div className="font-semibold">{reason.title || reason.level}</div>
                    <div className="mt-0.5 text-xs leading-5">{reason.detail || '-'}</div>
                  </div>
                ))}
              </div>
              <div className="overflow-hidden rounded border border-slate-200">
                <table className="min-w-full text-xs">
                  <tbody className="divide-y divide-slate-100">
                    <CoverageMetric label="원문 표시 계약" value={overview.reportedContractCount ?? '-'} />
                    <CoverageMetric label="표준 계약" value={overview.standardContractCount ?? '-'} />
                    <CoverageMetric label="담보 없는 계약" value={overview.noCoverageContractCount ?? 0} />
                    <CoverageMetric label="금액 없는 담보" value={overview.zeroAmountCoverageCount ?? 0} />
                    <CoverageMetric label="검수 후보" value={overview.reviewCandidateCount ?? 0} />
                  </tbody>
                </table>
              </div>
            </div>
          </section>

          {isSummary && (
            <section className="rounded-lg border border-slate-200 bg-white p-5 shadow-sm">
              <h2 className="text-base font-semibold text-slate-900">보장요약 결과</h2>
              <div className="mt-3 max-h-[420px] overflow-auto rounded border border-slate-200">
                <table className="min-w-[720px] text-xs">
                  <thead className="sticky top-0 bg-slate-50 text-slate-600"><tr><th className="px-2 py-2 text-left">분류</th><th className="px-2 py-2 text-left">보장</th><th className="px-2 py-2 text-right">합계</th><th className="px-2 py-2 text-right">상품수</th><th className="px-2 py-2 text-left">집계정책</th></tr></thead>
                  <tbody className="divide-y divide-slate-100">
                    {summaryRows.map(row => (
                      <tr key={row.coverageCode || row.coverageName}>
                        <td className="px-2 py-2 text-slate-600">{row.coverageGroup || row.normalizedCoverageGroup || '-'}</td>
                        <td className="px-2 py-2 font-medium text-slate-900">{row.coverageName || '-'}</td>
                        <td className="px-2 py-2 text-right tabular-nums">{row.totalAmount ? formatWon(row.totalAmount) : '-'}</td>
                        <td className="px-2 py-2 text-right tabular-nums">{Array.isArray(row.products) ? row.products.length : 0}</td>
                        <td className="px-2 py-2 text-slate-600">{row.aggregationPolicy?.label || '-'}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </section>
          )}

          <section className="rounded-lg border border-slate-200 bg-white p-5 shadow-sm">
            <h2 className="text-base font-semibold text-slate-900">계약/담보 추출 결과</h2>
            <div className="mt-3 space-y-2">
              {contracts.length === 0 ? (
                <div className="rounded border border-slate-200 bg-slate-50 px-3 py-8 text-center text-sm text-slate-500">추출된 계약이 없습니다.</div>
              ) : contracts.map((contract, idx) => {
                const coverages = Array.isArray(contract.coverages) ? contract.coverages : []
                return (
                  <details key={coverageContractKey(contract, idx)} className="rounded border border-slate-200" open={idx === 0}>
                    <summary className="cursor-pointer px-3 py-2 hover:bg-slate-50">
                      <span className="font-semibold text-slate-900">{contract.productName || '상품명 미추출'}</span>
                      <span className="ml-2 text-xs text-slate-500">{contract.insurerName || '보험사 미추출'} ? {contract.insuranceCategory || '-'} ? 월 {formatWon(contract.monthlyPremium)} ? 담보 {coverages.length}개</span>
                    </summary>
                    <div className="max-h-[360px] overflow-auto border-t border-slate-200">
                      <table className="min-w-[860px] text-xs">
                        <thead className="bg-slate-50 text-slate-600"><tr><th className="px-2 py-2 text-left">No</th><th className="px-2 py-2 text-left">회사담보명</th><th className="px-2 py-2 text-left">표준담보명</th><th className="px-2 py-2 text-left">분류</th><th className="px-2 py-2 text-right">가입금액</th></tr></thead>
                        <tbody className="divide-y divide-slate-100">
                          {coverages.map((coverage, coverageIdx) => (
                            <tr key={`${coverage.coverageNameRaw || coverage.standardCoverageName || coverageIdx}-${coverageIdx}`}>
                              <td className="px-2 py-2 text-slate-500">{coverageIdx + 1}</td>
                              <td className="px-2 py-2 text-slate-800">{coverage.coverageNameRaw || coverage.companyCoverageName || '-'}</td>
                              <td className="px-2 py-2 text-slate-700">{coverage.standardCoverageName || coverage.coverageNameNormalized || '-'}</td>
                              <td className="px-2 py-2 text-slate-600">{coverage.normalizedCoverageGroup || coverage.coverageGroup || '-'}</td>
                              <td className="px-2 py-2 text-right tabular-nums">{coverage.insuredAmount ? formatWon(coverage.insuredAmount) : '-'}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </details>
                )
              })}
            </div>
          </section>

          <details className="rounded-lg border border-slate-200 bg-white p-5 shadow-sm">
            <summary className="cursor-pointer text-base font-semibold text-slate-900">원문 JSON</summary>
            <pre className="mt-3 max-h-[520px] overflow-auto rounded bg-slate-950 p-4 text-xs leading-5 text-slate-100">{pretty(result)}</pre>
          </details>
        </>
      )}
    </div>
  )
}

function CoverageStat({ label, value }) {
  return (
    <div className="rounded border border-slate-200 bg-slate-50 px-3 py-2">
      <div className="text-[11px] text-slate-500">{label}</div>
      <div className="mt-0.5 text-lg font-semibold text-slate-900">{value}</div>
    </div>
  )
}

function CoverageMetric({ label, value }) {
  return (
    <tr>
      <td className="bg-slate-50 px-3 py-2 font-medium text-slate-600">{label}</td>
      <td className="px-3 py-2 text-right font-semibold text-slate-900">{value}</td>
    </tr>
  )
}

function coverageReasonClass(level) {
  if (level === 'ERROR') return 'border-rose-200 bg-rose-50 text-rose-800'
  if (level === 'WARN') return 'border-amber-200 bg-amber-50 text-amber-800'
  if (level === 'OK') return 'border-emerald-200 bg-emerald-50 text-emerald-800'
  return 'border-sky-200 bg-sky-50 text-sky-800'
}

function coverageContractKey(contract, idx) {
  return `${contract?.insurerName || ''}|${contract?.productName || ''}|${contract?.contractDate || ''}|${idx}`
}

const NOTIFICATION_TEST_TEXT = {
  titleDefault: '\u005b\uc6b4\uc601\ud14c\uc2a4\ud2b8\u005d \uc6f9\u002f\uc571\ud478\uc2dc \ud14c\uc2a4\ud2b8',
  bodyDefault: '\uc6b4\uc601\ud14c\uc2a4\ud2b8 \uba54\ub274\uc5d0\uc11c \ubc1c\uc1a1\ud55c \ud14c\uc2a4\ud2b8 \uc54c\ub9bc\uc785\ub2c8\ub2e4\u002e',
  loadError: '\uc54c\ub9bc \ud14c\uc2a4\ud2b8 \uc815\ubcf4\ub97c \ubd88\ub7ec\uc624\uc9c0 \ubabb\ud588\uc2b5\ub2c8\ub2e4\u002e',
  selectTemplate: '\ud14c\uc2a4\ud2b8 \ubc1c\uc1a1\ud560 \uc54c\ub9bc\ud1a1 \ud15c\ud50c\ub9bf\uc744 \uc120\ud0dd\ud574 \uc8fc\uc138\uc694\u002e',
  alimtalkKind: '\uc54c\ub9bc\ud1a1 \ud14c\uc2a4\ud2b8',
  alimtalkError: '\uc54c\ub9bc\ud1a1 \ud14c\uc2a4\ud2b8 \ubc1c\uc1a1\uc5d0 \uc2e4\ud328\ud588\uc2b5\ub2c8\ub2e4\u002e',
  pushBothKind: '\uc6f9\u002f\uc571\ud478\uc2dc \ud14c\uc2a4\ud2b8',
  appPushKind: '\uc571\ud478\uc2dc \ud14c\uc2a4\ud2b8',
  webPushKind: '\uc6f9 \uc54c\ub9bc \ud14c\uc2a4\ud2b8',
  pushError: '\uc6f9\u002f\uc571\ud478\uc2dc \ud14c\uc2a4\ud2b8 \ubc1c\uc1a1\uc5d0 \uc2e4\ud328\ud588\uc2b5\ub2c8\ub2e4\u002e',
}

function NotificationOperationTestPanel() {
  const t = NOTIFICATION_TEST_TEXT
  const [profile, setProfile] = React.useState(null)
  const [templates, setTemplates] = React.useState([])
  const [selectedTemplateId, setSelectedTemplateId] = React.useState('')
  const [title, setTitle] = React.useState(t.titleDefault)
  const [body, setBody] = React.useState(t.bodyDefault)
  const [link, setLink] = React.useState('/ops/operation-tests')
  const [loading, setLoading] = React.useState('')
  const [error, setError] = React.useState('')
  const [result, setResult] = React.useState(null)

  const loadData = React.useCallback(async () => {
    setError('')
    try {
      const [profileRes, templateRes] = await Promise.all([
        api.get('/ops/operation-tests/notifications/profile', { __silent: true }),
        api.get('/ops/operation-tests/notifications/alimtalk/templates', { __silent: true }),
      ])
      const profilePayload = profileRes?.data?.data || profileRes?.data || null
      const templateRows = templateRes?.data?.data || templateRes?.data || []
      setProfile(profilePayload)
      const nextTemplates = Array.isArray(templateRows) ? templateRows : []
      setTemplates(nextTemplates)
      setSelectedTemplateId(prev => {
        if (prev && nextTemplates.some(row => String(row.id) === String(prev))) return prev
        return nextTemplates[0]?.id ? String(nextTemplates[0].id) : ''
      })
    } catch (e) {
      setError(getApiErrorMessage(e, t.loadError))
    }
  }, [t.loadError])

  React.useEffect(() => {
    loadData()
  }, [loadData])

  const sendAlimtalk = async () => {
    setError('')
    setResult(null)
    if (!selectedTemplateId) {
      setError(t.selectTemplate)
      return
    }
    setLoading('alimtalk')
    try {
      const res = await api.post('/ops/operation-tests/notifications/alimtalk', {
        templateId: Number(selectedTemplateId),
      }, { __silent: true })
      setResult({ kind: t.alimtalkKind, payload: res?.data?.data || res?.data || null })
      await loadData()
    } catch (e) {
      setError(getApiErrorMessage(e, t.alimtalkError))
    } finally {
      setLoading('')
    }
  }

  const sendPush = async (channels) => {
    setError('')
    setResult(null)
    const loadingKey = channels.appEnabled && channels.webEnabled ? 'push-both' : (channels.appEnabled ? 'push-app' : 'push-web')
    setLoading(loadingKey)
    try {
      const res = await api.post('/ops/operation-tests/notifications/push', {
        webEnabled: Boolean(channels.webEnabled),
        appEnabled: Boolean(channels.appEnabled),
        title,
        body,
        link,
      }, { __silent: true })
      setResult({
        kind: channels.appEnabled && channels.webEnabled ? t.pushBothKind : (channels.appEnabled ? t.appPushKind : t.webPushKind),
        payload: res?.data?.data || res?.data || null,
      })
      await loadData()
    } catch (e) {
      setError(getApiErrorMessage(e, t.pushError))
    } finally {
      setLoading('')
    }
  }

  const selectedTemplate = templates.find(row => String(row.id) === String(selectedTemplateId))
  const resultPayload = result?.payload || null
  const isAlimtalkSuccess = resultPayload && Object.prototype.hasOwnProperty.call(resultPayload, 'success')
    ? Boolean(resultPayload.success)
    : null

  return (
    <div className="space-y-5">
      <section className="rounded-lg border border-slate-200 bg-white p-5 shadow-sm">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <div>
            <h2 className="text-base font-semibold text-slate-900">{'\uc54c\ub9bc\ud1a1 \ud14c\uc2a4\ud2b8'}</h2>
            <div className="mt-0.5 text-xs text-slate-500">{'\ub85c\uadf8\uc778\ud55c \uc6b4\uc601\uc790 \ubcf8\uc778\uc5d0\uac8c \uc2e4\uc81c \ud14c\uc2a4\ud2b8 \uc54c\ub9bc\uc744 \ubc1c\uc1a1\ud569\ub2c8\ub2e4\u002e'}</div>
          </div>
          <button
            type="button"
            className="rounded border border-slate-300 bg-white px-3 py-2 text-sm font-semibold text-slate-700 hover:bg-slate-50 disabled:opacity-50"
            disabled={Boolean(loading)}
            onClick={loadData}
          >
            {'\uc0c1\ud0dc \uc0c8\ub85c\uace0\uce68'}
          </button>
        </div>
        <div className="mt-4 grid gap-3 md:grid-cols-4">
          <NotificationTestMetric label={'\uc6b4\uc601\uc790'} value={profile?.name || profile?.email || '-'} />
          <NotificationTestMetric label={'\ud734\ub300\ud3f0'} value={profile?.hasPhoneNumber ? (profile?.phoneMasked || '\ub4f1\ub85d\ub428') : '\ubbf8\ub4f1\ub85d'} tone={profile?.hasPhoneNumber ? 'ok' : 'warn'} />
          <NotificationTestMetric label={'\uc571 \ud1a0\ud070'} value={`${Number(profile?.activePushTokenCount || 0).toLocaleString()}\uac1c`} tone={Number(profile?.activePushTokenCount || 0) > 0 ? 'ok' : 'warn'} />
          <NotificationTestMetric label={'\uc6f9 \uc5f0\uacb0'} value={`${Number(profile?.webConnectedEmitters || 0).toLocaleString()}\uac1c`} tone={Number(profile?.webConnectedEmitters || 0) > 0 ? 'ok' : 'warn'} />
        </div>
        {error && <div className="mt-4 rounded border border-rose-200 bg-rose-50 px-3 py-2 text-sm text-rose-700">{error}</div>}
      </section>

      <section className="grid gap-5 lg:grid-cols-2">
        <div className="rounded-lg border border-slate-200 bg-white p-5 shadow-sm">
          <div className="flex flex-wrap items-start justify-between gap-3">
            <div>
              <h2 className="text-base font-semibold text-slate-900">{'\uc54c\ub9bc\ud1a1 \ud14c\uc2a4\ud2b8'}</h2>
              <div className="mt-0.5 text-xs text-slate-500">{'\uc120\ud0dd\ud55c \ud15c\ud50c\ub9bf\uc758 \ub370\ubaa8 \ubcc0\uc218\ub85c \ubcf8\uc778 \ud734\ub300\ud3f0\uc5d0 \ubc1c\uc1a1\ud569\ub2c8\ub2e4\u002e'}</div>
            </div>
            <span className="rounded-full bg-slate-100 px-2.5 py-1 text-xs font-semibold text-slate-600">{`${templates.length}\uac1c \ud15c\ud50c\ub9bf`}</span>
          </div>
          <label className="mt-4 block text-sm">
            <span className="text-xs font-medium text-slate-600">{'\ud14c\uc2a4\ud2b8 \ud15c\ud50c\ub9bf'}</span>
            <select
              className="mt-1 w-full rounded border border-slate-200 px-3 py-2 text-sm focus:border-brand-navy focus:outline-none focus:ring-1 focus:ring-brand-navy/20"
              value={selectedTemplateId}
              onChange={e => setSelectedTemplateId(e.target.value)}
            >
              {templates.length === 0 ? (
                <option value="">{'\ud65c\uc131 \ud15c\ud50c\ub9bf \uc5c6\uc74c'}</option>
              ) : templates.map(template => (
                <option key={template.id} value={template.id}>
                  {template.templateName || template.templateCode || `\ud15c\ud50c\ub9bf #${template.id}`}
                </option>
              ))}
            </select>
          </label>
          {selectedTemplate && (
            <div className="mt-3 rounded border border-slate-200 bg-slate-50 px-3 py-2 text-xs text-slate-600">
              <div className="font-semibold text-slate-800">{selectedTemplate.templateCode}</div>
              <div className="mt-0.5">{selectedTemplate.kakaoVendor || '\uacf5\uae09\uc0ac \uc790\ub3d9'} · {selectedTemplate.description || '\uc124\uba85 \uc5c6\uc74c'}</div>
            </div>
          )}
          <button
            type="button"
            className="mt-4 rounded bg-brand-navy px-4 py-2 text-sm font-semibold text-white hover:opacity-95 disabled:opacity-50"
            disabled={loading === 'alimtalk' || !selectedTemplateId || !profile?.hasPhoneNumber}
            onClick={sendAlimtalk}
          >
            {loading === 'alimtalk' ? '\ubc1c\uc1a1 \uc911\u002e\u002e\u002e' : '\uc54c\ub9bc\ud1a1 \ud14c\uc2a4\ud2b8 \ubc1c\uc1a1'}
          </button>
        </div>

        <div className="rounded-lg border border-slate-200 bg-white p-5 shadow-sm">
          <h2 className="text-base font-semibold text-slate-900">{'\uc6f9\u002f\uc571\ud478\uc2dc \ud14c\uc2a4\ud2b8'}</h2>
          <div className="mt-4 grid gap-3">
            <label className="block text-sm">
              <span className="text-xs font-medium text-slate-600">{'\uc81c\ubaa9'}</span>
              <input className="mt-1 w-full rounded border border-slate-200 px-3 py-2 text-sm focus:border-brand-navy focus:outline-none focus:ring-1 focus:ring-brand-navy/20" value={title} onChange={e => setTitle(e.target.value)} />
            </label>
            <label className="block text-sm">
              <span className="text-xs font-medium text-slate-600">{'\ubcf8\ubb38'}</span>
              <textarea className="mt-1 min-h-[86px] w-full rounded border border-slate-200 px-3 py-2 text-sm focus:border-brand-navy focus:outline-none focus:ring-1 focus:ring-brand-navy/20" value={body} onChange={e => setBody(e.target.value)} />
            </label>
            <label className="block text-sm">
              <span className="text-xs font-medium text-slate-600">{'\uc5f0\uacb0 \uacbd\ub85c'}</span>
              <input className="mt-1 w-full rounded border border-slate-200 px-3 py-2 text-sm focus:border-brand-navy focus:outline-none focus:ring-1 focus:ring-brand-navy/20" value={link} onChange={e => setLink(e.target.value)} />
            </label>
          </div>
          <div className="mt-4 flex flex-wrap gap-2">
            <button type="button" className="rounded border border-slate-300 bg-white px-3 py-2 text-sm font-semibold text-slate-700 hover:bg-slate-50 disabled:opacity-50" disabled={Boolean(loading)} onClick={() => sendPush({ webEnabled: true, appEnabled: false })}>
              {loading === 'push-web' ? '\ubc1c\uc1a1 \uc911\u002e\u002e\u002e' : '\uc6f9 \uc54c\ub9bc \ud14c\uc2a4\ud2b8'}
            </button>
            <button type="button" className="rounded border border-slate-300 bg-white px-3 py-2 text-sm font-semibold text-slate-700 hover:bg-slate-50 disabled:opacity-50" disabled={Boolean(loading)} onClick={() => sendPush({ webEnabled: false, appEnabled: true })}>
              {loading === 'push-app' ? '\ubc1c\uc1a1 \uc911\u002e\u002e\u002e' : '\uc571\ud478\uc2dc \ud14c\uc2a4\ud2b8'}
            </button>
            <button type="button" className="rounded bg-brand-navy px-4 py-2 text-sm font-semibold text-white hover:opacity-95 disabled:opacity-50" disabled={Boolean(loading)} onClick={() => sendPush({ webEnabled: true, appEnabled: true })}>
              {loading === 'push-both' ? '\ubc1c\uc1a1 \uc911\u002e\u002e\u002e' : '\uc6f9\u002b\uc571 \ub3d9\uc2dc \ud14c\uc2a4\ud2b8'}
            </button>
          </div>
        </div>
      </section>

      {result && (
        <section className="rounded-lg border border-slate-200 bg-white p-5 shadow-sm">
          <div className="flex flex-wrap items-center justify-between gap-2">
            <div>
              <h2 className="text-base font-semibold text-slate-900">{`${result.kind} \uacb0\uacfc`}</h2>
              <div className="mt-0.5 text-xs text-slate-500">{resultPayload?.recipient?.email || profile?.email || '-'}</div>
            </div>
            {isAlimtalkSuccess != null && (
              <span className={`rounded-full px-3 py-1 text-xs font-semibold ${isAlimtalkSuccess ? 'bg-emerald-50 text-emerald-700' : 'bg-rose-50 text-rose-700'}`}>
                {isAlimtalkSuccess ? '\uc131\uacf5' : '\uc2e4\ud328'}
              </span>
            )}
          </div>
          <div className="mt-4 grid gap-3 md:grid-cols-3">
            <NotificationTestMetric label={'\ub300\uc0c1'} value={resultPayload?.recipient?.name || resultPayload?.recipient?.email || '-'} />
            <NotificationTestMetric label={'\uc6f9 \uc5f0\uacb0'} value={`${Number(resultPayload?.web?.connectedEmitters ?? profile?.webConnectedEmitters ?? 0).toLocaleString()}\uac1c`} />
            <NotificationTestMetric label={'\uc571 \ud1a0\ud070'} value={`${Number(resultPayload?.app?.tokenCount ?? profile?.activePushTokenCount ?? 0).toLocaleString()}\uac1c`} />
          </div>
          <pre className="mt-4 max-h-[420px] overflow-auto rounded bg-slate-950 p-4 text-xs leading-5 text-slate-100">{pretty(resultPayload)}</pre>
        </section>
      )}
    </div>
  )
}

function NotificationTestMetric({ label, value, tone = 'default' }) {
  const toneClass = tone === 'ok'
    ? 'border-emerald-200 bg-emerald-50 text-emerald-900'
    : tone === 'warn'
      ? 'border-amber-200 bg-amber-50 text-amber-900'
      : 'border-slate-200 bg-slate-50 text-slate-900'
  return (
    <div className={`rounded border px-3 py-2 ${toneClass}`}>
      <div className="text-[11px] font-medium text-slate-500">{label}</div>
      <div className="mt-0.5 truncate text-sm font-semibold">{value}</div>
    </div>
  )
}
function FhirOperationTestPanel() {
  const [reports, setReports] = React.useState([])
  const [selectedReportId, setSelectedReportId] = React.useState('')
  const [loadingReports, setLoadingReports] = React.useState(false)
  const [running, setRunning] = React.useState(false)
  const [error, setError] = React.useState('')
  const [result, setResult] = React.useState(null)

  const loadReports = React.useCallback(async () => {
    setLoadingReports(true)
    setError('')
    try {
      const res = await api.get('/ops/operation-tests/fhir/health-reports?limit=30', { __silent: true })
      const payload = res?.data?.data || res?.data || {}
      const rows = Array.isArray(payload?.reports) ? payload.reports : []
      setReports(rows)
      setSelectedReportId(prev => prev || (rows[0]?.id ? String(rows[0].id) : ''))
    } catch (e) {
      setError(getApiErrorMessage(e, 'FHIR 테스트용 리포트 목록을 불러오지 못했습니다.'))
    } finally {
      setLoadingReports(false)
    }
  }, [])

  React.useEffect(() => {
    loadReports()
  }, [loadReports])

  const runMapping = async () => {
    setError('')
    if (!selectedReportId) {
      setError('FHIR 매핑을 실행할 리포트를 선택해 주세요.')
      return
    }
    setRunning(true)
    try {
      const res = await api.post('/ops/operation-tests/fhir/mapping-runs', {
        healthReportId: Number(selectedReportId),
      })
      setResult(res?.data?.data || res?.data || null)
    } catch (e) {
      setError(getApiErrorMessage(e, 'FHIR 매핑 테스트 실행에 실패했습니다.'))
    } finally {
      setRunning(false)
    }
  }

  const selectedReport = reports.find(row => String(row.id) === String(selectedReportId))
  const resources = Array.isArray(result?.resources) ? result.resources : []
  const issues = Array.isArray(result?.issues) ? result.issues : []
  const checks = Array.isArray(result?.consistencyChecks) ? result.consistencyChecks : []
  const summary = result?.summary || {}
  const firstResource = resources[0]
  const mismatchCount = summary.mismatchCount ?? checks.filter(row => row.result === 'MISMATCH').length

  return (
    <section className="grid gap-5 lg:grid-cols-[minmax(0,0.9fr)_minmax(0,1.1fr)]">
      <div className="rounded-lg border border-slate-200 bg-white p-5 shadow-sm">
        <div className="flex items-start justify-between gap-3">
          <div>
            <h2 className="text-base font-semibold text-slate-900">FHIR 테스트</h2>
            <p className="mt-1 text-xs text-slate-500">진료정보분석 리포트를 FHIR R4 shadow 데이터로 변환하고 기존 read model과 일치 여부를 확인합니다.</p>
          </div>
          <button
            type="button"
            className="rounded border border-slate-200 px-3 py-2 text-xs font-semibold text-slate-700 hover:bg-slate-50 disabled:opacity-50"
            disabled={loadingReports}
            onClick={loadReports}
          >
            {loadingReports ? '불러오는 중...' : '새로고침'}
          </button>
        </div>

        <label className="mt-4 block text-sm">
          <span className="text-xs font-medium text-slate-600">테스트 리포트</span>
          <select
            className="mt-1 w-full rounded border px-3 py-2 text-sm"
            value={selectedReportId}
            onChange={e => { setSelectedReportId(e.target.value); setResult(null); setError('') }}
            disabled={loadingReports || reports.length === 0}
          >
            {reports.length === 0 ? (
              <option value="">리포트 없음</option>
            ) : reports.map(row => (
              <option key={row.id} value={row.id}>
                #{row.id} · {row.customerName || '이름 없음'} · {(row.createdAt || '').slice(0, 10) || '-'}
              </option>
            ))}
          </select>
        </label>

        {selectedReport && (
          <div className="mt-4 grid gap-2 sm:grid-cols-3">
            <div className="rounded-lg bg-slate-50 p-3">
              <div className="text-xs text-slate-500">리포트</div>
              <div className="mt-1 font-mono text-sm font-semibold text-slate-900">#{selectedReport.id}</div>
            </div>
            <div className="rounded-lg bg-slate-50 p-3">
              <div className="text-xs text-slate-500">생성일</div>
              <div className="mt-1 text-sm font-semibold text-slate-900">{(selectedReport.createdAt || '').slice(0, 10) || '-'}</div>
            </div>
            <div className="rounded-lg bg-slate-50 p-3">
              <div className="text-xs text-slate-500">위험도</div>
              <div className="mt-1 text-sm font-semibold text-slate-900">{selectedReport.riskLevel || '-'} {selectedReport.riskScore ?? ''}</div>
            </div>
          </div>
        )}

        <div className="mt-5 flex flex-wrap gap-2">
          <button
            type="button"
            className="rounded bg-brand-navy px-4 py-2 text-sm font-semibold text-white hover:opacity-95 disabled:opacity-50"
            disabled={running || loadingReports || !selectedReportId}
            onClick={runMapping}
          >
            {running ? 'FHIR 매핑 중...' : 'FHIR 매핑 실행'}
          </button>
        </div>

        {error && <div className="mt-4 rounded border border-rose-200 bg-rose-50 px-3 py-2 text-sm text-rose-700">{error}</div>}

        <div className="mt-5 rounded-lg border border-amber-100 bg-amber-50 px-3 py-2 text-xs text-amber-800">
          생성된 FHIR 데이터는 별도 shadow 테이블에 저장되며 기존 리포트/청구/추천 플로우에는 반영하지 않습니다.
        </div>
      </div>

      <div className="space-y-4">
        <div className="rounded-lg border border-slate-200 bg-white p-5 shadow-sm">
          <h3 className="text-sm font-semibold text-slate-900">실행 결과</h3>
          {result ? (
            <>
              <div className="mt-3 grid gap-2 sm:grid-cols-4">
                <div className="rounded-lg bg-slate-50 p-3">
                  <div className="text-xs text-slate-500">상태</div>
                  <div className="mt-1 text-sm font-semibold text-slate-900">{result.run?.status || '-'}</div>
                </div>
                <div className="rounded-lg bg-slate-50 p-3">
                  <div className="text-xs text-slate-500">FHIR 리소스</div>
                  <div className="mt-1 text-sm font-semibold text-slate-900">{summary.resourceCount ?? resources.length}</div>
                </div>
                <div className="rounded-lg bg-slate-50 p-3">
                  <div className="text-xs text-slate-500">매핑 이슈</div>
                  <div className="mt-1 text-sm font-semibold text-slate-900">{summary.issueCount ?? issues.length}</div>
                </div>
                <div className="rounded-lg bg-slate-50 p-3">
                  <div className="text-xs text-slate-500">불일치</div>
                  <div className="mt-1 text-sm font-semibold text-slate-900">{mismatchCount}</div>
                </div>
              </div>

              <div className="mt-4 overflow-auto rounded border border-slate-200">
                <table className="min-w-full divide-y divide-slate-200 text-xs">
                  <thead className="bg-slate-50 text-slate-500">
                    <tr>
                      <th className="px-3 py-2 text-left font-semibold">일치성 체크</th>
                      <th className="px-3 py-2 text-left font-semibold">예상</th>
                      <th className="px-3 py-2 text-left font-semibold">실제</th>
                      <th className="px-3 py-2 text-left font-semibold">결과</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-100 bg-white">
                    {checks.map(row => (
                      <tr key={row.id || row.checkType}>
                        <td className="px-3 py-2 font-mono text-slate-700">{row.checkType}</td>
                        <td className="px-3 py-2 text-slate-700">{row.expectedValue ?? '-'}</td>
                        <td className="px-3 py-2 text-slate-700">{row.actualValue ?? '-'}</td>
                        <td className={`px-3 py-2 font-semibold ${row.result === 'MATCH' ? 'text-emerald-700' : 'text-rose-700'}`}>{row.result}</td>
                      </tr>
                    ))}
                    {checks.length === 0 && <tr><td colSpan="4" className="px-3 py-6 text-center text-slate-500">아직 실행 결과가 없습니다.</td></tr>}
                  </tbody>
                </table>
              </div>

              <div className="mt-4 overflow-auto rounded border border-slate-200">
                <table className="min-w-full divide-y divide-slate-200 text-xs">
                  <thead className="bg-slate-50 text-slate-500">
                    <tr>
                      <th className="px-3 py-2 text-left font-semibold">리소스</th>
                      <th className="px-3 py-2 text-left font-semibold">출처</th>
                      <th className="px-3 py-2 text-left font-semibold">상태</th>
                      <th className="px-3 py-2 text-left font-semibold">키</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-100 bg-white">
                    {resources.slice(0, 80).map(row => (
                      <tr key={row.id}>
                        <td className="px-3 py-2 font-semibold text-slate-800">{row.resourceType}</td>
                        <td className="px-3 py-2 font-mono text-slate-600">{row.sourceId}</td>
                        <td className="px-3 py-2 text-slate-700">{row.validationStatus}</td>
                        <td className="px-3 py-2 font-mono text-slate-600">{row.resourceKey}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              {issues.length > 0 && (
                <div className="mt-4 overflow-auto rounded border border-slate-200">
                  <table className="min-w-full divide-y divide-slate-200 text-xs">
                    <thead className="bg-slate-50 text-slate-500">
                      <tr>
                        <th className="px-3 py-2 text-left font-semibold">심각도</th>
                        <th className="px-3 py-2 text-left font-semibold">유형</th>
                        <th className="px-3 py-2 text-left font-semibold">필드</th>
                        <th className="px-3 py-2 text-left font-semibold">메시지</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-slate-100 bg-white">
                      {issues.map(row => (
                        <tr key={row.id || `${row.issueType}-${row.sourceId}`}>
                          <td className={`px-3 py-2 font-semibold ${row.severity === 'ERROR' ? 'text-rose-700' : 'text-amber-700'}`}>{row.severity}</td>
                          <td className="px-3 py-2 font-mono text-slate-700">{row.issueType}</td>
                          <td className="px-3 py-2 text-slate-700">{row.sourceField || '-'}</td>
                          <td className="px-3 py-2 text-slate-700">{row.message}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}

              <div className="mt-4 rounded-lg border border-slate-200 bg-slate-950 p-3">
                <div className="mb-2 text-xs font-semibold text-slate-300">FHIR JSON 미리보기</div>
                <pre className="max-h-[420px] overflow-auto text-xs text-slate-100">{firstResource ? pretty(firstResource.fhirJson) : '생성된 리소스가 없습니다.'}</pre>
              </div>
            </>
          ) : (
            <div className="mt-3 rounded bg-slate-50 px-3 py-8 text-center text-sm text-slate-500">FHIR 매핑 실행 결과가 없습니다.</div>
          )}
        </div>
      </div>
    </section>
  )
}
function ClaimOperationTestPanel() {
  const [insurers, setInsurers] = React.useState([])
  const [selectedIds, setSelectedIds] = React.useState([])
  const [runningMode, setRunningMode] = React.useState('')
  const [inputMode, setInputMode] = React.useState('AUTO_DUMMY')
  const [testFaxNumber, setTestFaxNumber] = React.useState(DEFAULT_CLAIM_TEST_FAX_NUMBER)
  const [title, setTitle] = React.useState('\uBCF4\uD5D8\uAE08\uCCAD\uAD6C \uC6B4\uC601\uD14C\uC2A4\uD2B8')
  const [loading, setLoading] = React.useState(false)
  const [downloading, setDownloading] = React.useState('')
  const [fieldDraftLoading, setFieldDraftLoading] = React.useState(false)
  const [fieldDrafts, setFieldDrafts] = React.useState([])
  const [fieldDraftError, setFieldDraftError] = React.useState('')
  const [manualValues, setManualValues] = React.useState({})
  const [manualFilter, setManualFilter] = React.useState('')
  const [error, setError] = React.useState('')
  const [result, setResult] = React.useState(null)
  const [attachments, setAttachments] = React.useState([])
  const [attachmentUploading, setAttachmentUploading] = React.useState(false)
  const [attachmentError, setAttachmentError] = React.useState('')
  const [cleanupMessage, setCleanupMessage] = React.useState('')
  const [cleaningFiles, setCleaningFiles] = React.useState(false)

  const selectedSet = React.useMemo(() => new Set(selectedIds.map(String)), [selectedIds])
  const groupedManualFields = React.useMemo(() => groupFieldDrafts(fieldDrafts, manualFilter), [fieldDrafts, manualFilter])
  const attachmentPageCount = React.useMemo(() => attachments.reduce((sum, item) => sum + (Number(item.pageCount) || 0), 0), [attachments])
  const successCount = result?.successCount || 0
  const failedCount = result?.failedCount || 0
  const cleanupFileIds = React.useMemo(() => {
    const ids = []
    attachments.forEach(item => {
      if (item?.fileId) ids.push(item.fileId)
    })
    ;(asArray(result?.results)).forEach(row => {
      if (row?.fileId) ids.push(row.fileId)
    })
    return Array.from(new Set(ids.filter(Boolean)))
  }, [attachments, result])

  const loadInsurers = React.useCallback(async () => {
    setError('')
    try {
      const res = await api.get('/ops/operation-tests/claims/insurers', { __silent: true })
      const rows = res?.data?.data || res?.data || []
      const nextRows = Array.isArray(rows) ? rows : []
      setInsurers(nextRows)
      setSelectedIds(prev => prev.filter(id => nextRows.some(insurer =>
        String(insurer.insurerOrgId) === String(id) && isClaimTestInsurerSelectable(insurer))))
    } catch (e) {
      setError(getApiErrorMessage(e, '보험사 목록을 불러오지 못했습니다.'))
    }
  }, [])

  React.useEffect(() => {
    loadInsurers()
  }, [loadInsurers])

  const loadFieldDrafts = React.useCallback(async () => {
    if (selectedIds.length === 0) {
      setFieldDrafts([])
      setManualValues({})
      setFieldDraftError('')
      return
    }
    setFieldDraftLoading(true)
    setFieldDraftError('')
    try {
      const res = await api.post('/ops/operation-tests/claims/fields', {
        insurerOrgIds: selectedIds,
      }, { __silent: true })
      const rows = res?.data?.data || res?.data || []
      const nextRows = Array.isArray(rows) ? rows : []
      setFieldDrafts(nextRows)
      setManualValues(prev => {
        const next = {}
        nextRows.forEach(field => {
          if (!field?.key) return
          next[field.key] = Object.prototype.hasOwnProperty.call(prev, field.key)
            ? prev[field.key]
            : (field.sampleValue || '')
        })
        return next
      })
    } catch (e) {
      setFieldDraftError(getApiErrorMessage(e, '직접 입력 필드를 불러오지 못했습니다.'))
    } finally {
      setFieldDraftLoading(false)
    }
  }, [selectedIds])

  React.useEffect(() => {
    if (inputMode === 'MANUAL') {
      loadFieldDrafts()
    }
  }, [inputMode, loadFieldDrafts])

  const toggleInsurer = (id) => {
    const insurer = insurers.find(item => String(item.insurerOrgId) === String(id))
    if (!isClaimTestInsurerSelectable(insurer)) return
    const key = String(id)
    setSelectedIds(prev => {
      const exists = prev.some(x => String(x) === key)
      return exists ? prev.filter(x => String(x) !== key) : [...prev, id]
    })
  }

  const selectAll = () => setSelectedIds(insurers.filter(isClaimTestInsurerSelectable).map(x => x.insurerOrgId).filter(Boolean))
  const clearSelected = () => setSelectedIds([])
  const updateManualValue = (key, value) => setManualValues(prev => ({ ...prev, [key]: value }))
  const resetManualSamples = () => {
    setManualValues(fieldDrafts.reduce((acc, field) => {
      if (field?.key) acc[field.key] = field.sampleValue || ''
      return acc
    }, {}))
  }
  const clearManualValues = () => {
    setManualValues(fieldDrafts.reduce((acc, field) => {
      if (field?.key) acc[field.key] = ''
      return acc
    }, {}))
  }

  const uploadAttachments = async (event) => {
    const files = Array.from(event.target.files || [])
    event.target.value = ''
    if (files.length === 0) return
    setAttachmentError('')
    setCleanupMessage('')
    setAttachmentUploading(true)
    try {
      const uploaded = []
      for (const file of files) {
        const formData = new FormData()
        formData.append('file', file)
        const res = await api.post('/files/upload/claim-document', formData, {
          headers: { 'Content-Type': 'multipart/form-data' },
          __silent: true,
        })
        const payload = (res?.data && res.data.data !== undefined) ? res.data.data : res.data
        const fileId = payload?.fileId || payload?.id
        if (!fileId) throw new Error('UPLOAD_FAILED')
        uploaded.push({
          fileId,
          originalName: payload?.originalName || file.name,
          size: payload?.size || file.size,
          originalSize: payload?.originalSize || file.size,
          pageCount: Number(payload?.pageCount) > 0 ? Number(payload.pageCount) : 1,
        })
      }
      setAttachments(prev => [...prev, ...uploaded])
    } catch (e) {
      setAttachmentError(getApiErrorMessage(e, '첨부파일 업로드에 실패했습니다.'))
    } finally {
      setAttachmentUploading(false)
    }
  }

  const removeAttachment = (fileId) => {
    setAttachments(prev => prev.filter(item => item.fileId !== fileId))
  }

  const clearAttachments = () => setAttachments([])

  const cleanupTestFiles = async () => {
    if (cleanupFileIds.length === 0) {
      setCleanupMessage('정리할 테스트 파일이 없습니다.')
      return
    }
    if (!window.confirm(`현재 화면의 테스트 파일 ${cleanupFileIds.length}개를 정리할까요?`)) return
    setCleaningFiles(true)
    setError('')
    setAttachmentError('')
    setCleanupMessage('')
    try {
      const res = await api.post('/ops/operation-tests/claims/cleanup', {
        fileIds: cleanupFileIds,
      }, { __silent: true })
      const payload = res?.data?.data || res?.data || {}
      const deletedCount = Number(payload.deletedCount || 0)
      const skippedCount = Number(payload.skippedCount || 0)
      setCleanupMessage(skippedCount > 0 ? `테스트 파일 ${deletedCount}개를 정리했습니다. (${skippedCount}개 제외)` : `테스트 파일 ${deletedCount}개를 정리했습니다.`)
      setAttachments([])
      setResult(null)
    } catch (e) {
      setError(getApiErrorMessage(e, '테스트 파일 정리에 실패했습니다.'))
    } finally {
      setCleaningFiles(false)
    }
  }

  const runTest = async (requestedMode) => {
    const mode = requestedMode || 'PDF_ONLY'
    setError('')
    setCleanupMessage('')
    setResult(null)
    if (attachmentUploading) {
      setError('첨부파일 업로드가 끝난 뒤 실행해 주세요.')
      return
    }
    if (selectedIds.length === 0) {
      setError('테스트할 보험사를 선택해 주세요.')
      return
    }
    if (isClaimTestSendMode(mode) && !String(testFaxNumber || '').replace(/[^0-9]/g, '')) {
      setError('테스트 팩스 발송을 선택한 경우 수신 팩스번호를 입력해 주세요.')
      return
    }
    if (inputMode === 'MANUAL') {
      if (fieldDraftLoading) {
        setError('직접 입력 필드를 불러오는 중입니다.')
        return
      }
      if (fieldDrafts.length === 0) {
        setError('직접 입력 필드를 먼저 불러와 주세요.')
        return
      }
    }
    setLoading(true)
    setRunningMode(mode)
    try {
      const res = await api.post('/ops/operation-tests/claims/run', {
        insurerOrgIds: selectedIds,
        mode,
        inputMode,
        testFaxNumber,
        title,
        attachmentFileIds: attachments.map(item => item.fileId).filter(Boolean),
        overrides: inputMode === 'MANUAL' ? manualValues : undefined,
      }, { __silent: true })
      setResult(res?.data?.data || res?.data || null)
    } catch (e) {
      setError(getApiErrorMessage(e, '보험금청구 운영테스트 실행에 실패했습니다.'))
    } finally {
      setLoading(false)
      setRunningMode('')
    }
  }

  const downloadPdf = async (row) => {
    if (!row?.fileId) return
    const fileId = canonicalStoredFileIdForApiPath(row.fileId)
    setDownloading(row.fileId)
    setError('')
    try {
      const res = await api.get(`/files/${encodeURIComponent(fileId)}/download`, { responseType: 'blob', __silent: true })
      downloadBlob(res.data, row.filename || claimTestFallbackFilename(row))
    } catch (e) {
      setError(getApiErrorMessage(e, '테스트 파일을 다운로드하지 못했습니다.'))
    } finally {
      setDownloading('')
    }
  }

  return (
    <div className="space-y-5">
      <section className="rounded-lg border border-slate-200 bg-white p-5 shadow-sm">
        <div className="grid gap-4 lg:grid-cols-[minmax(0,0.8fr)_minmax(0,1fr)]">
          <div>
            <h2 className="text-base font-semibold text-slate-900">보험금청구 테스트</h2>
            <div className="mt-4 grid gap-3 sm:grid-cols-2">
              <label className="block text-sm">
                <span className="text-xs font-medium text-slate-600">입력 데이터</span>
                <select className="mt-1 w-full rounded border px-3 py-2 text-sm" value={inputMode} onChange={e => setInputMode(e.target.value)}>
                  <option value="AUTO_DUMMY">더미데이터 자동 생성</option>
                  <option value="MANUAL">직접 필드 입력</option>
                </select>
              </label>
              <label className="block text-sm">
                <span className="text-xs font-medium text-slate-600">테스트 팩스번호</span>
                <input
                  className="mt-1 w-full rounded border px-3 py-2 text-sm"
                  value={testFaxNumber}
                  onChange={e => setTestFaxNumber(e.target.value)}
                  placeholder="0505 또는 일반 팩스번호"
                />
              </label>
            </div>
            <label className="mt-3 block text-sm">
              <span className="text-xs font-medium text-slate-600">팩스 제목</span>
              <input className="mt-1 w-full rounded border px-3 py-2 text-sm" value={title} onChange={e => setTitle(e.target.value)} />
            </label>
            <div className="mt-3 rounded border border-slate-200 bg-slate-50 p-3">
              <div className="flex flex-wrap items-center justify-between gap-2">
                <div>
                  <div className="text-xs font-semibold text-slate-700">운영테스트 첨부파일</div>
                  <div className="mt-0.5 text-xs text-slate-500">{attachments.length}개 · {attachmentPageCount}장</div>
                </div>
                <div className="flex flex-wrap gap-2">
                  <label className={`cursor-pointer rounded border border-slate-300 bg-white px-3 py-1.5 text-xs font-semibold text-slate-700 hover:bg-slate-50 ${attachmentUploading ? 'pointer-events-none opacity-50' : ''}`}>
                    {attachmentUploading ? '업로드 중...' : '파일 추가'}
                    <input
                      type="file"
                      className="hidden"
                      multiple
                      accept="application/pdf,image/jpeg,image/png,.pdf,.jpg,.jpeg,.png"
                      disabled={attachmentUploading}
                      onChange={uploadAttachments}
                    />
                  </label>
                  {attachments.length > 0 && (
                    <button type="button" className="rounded border border-slate-300 bg-white px-3 py-1.5 text-xs font-semibold text-slate-700 hover:bg-slate-50" onClick={clearAttachments}>전체 삭제</button>
                  )}
                </div>
              </div>
              {attachmentError && <div className="mt-2 rounded border border-rose-200 bg-rose-50 px-3 py-2 text-xs text-rose-700">{attachmentError}</div>}
              {attachments.length > 0 && (
                <div className="mt-3 max-h-36 overflow-auto rounded border border-slate-200 bg-white">
                  {attachments.map(item => (
                    <div key={item.fileId} className="flex items-center justify-between gap-3 border-b border-slate-100 px-3 py-2 last:border-b-0">
                      <div className="min-w-0">
                        <div className="truncate text-xs font-medium text-slate-800">{item.originalName || item.fileId}</div>
                        <div className="mt-0.5 text-[11px] text-slate-500">{Number(item.pageCount || 0)}장 · {formatBytes(item.size)}</div>
                      </div>
                      <button type="button" className="shrink-0 rounded border border-slate-200 px-2 py-1 text-[11px] text-slate-600 hover:bg-slate-50" onClick={() => removeAttachment(item.fileId)}>삭제</button>
                    </div>
                  ))}
                </div>
              )}
            </div>
            <div className="mt-4 flex flex-wrap gap-2">
              {CLAIM_TEST_MODE_OPTIONS.map(option => (
                <button
                  key={option.value}
                  type="button"
                  className={`rounded px-4 py-2 text-sm font-semibold disabled:opacity-50 ${
                    isClaimTestSendMode(option.value)
                      ? 'bg-brand-navy text-white hover:opacity-95'
                      : 'border border-slate-300 bg-white text-slate-700 hover:bg-slate-50'
                  }`}
                  disabled={loading || attachmentUploading}
                  onClick={() => runTest(option.value)}
                >
                  {runningMode === option.value ? '실행 중...' : option.label}
                </button>
              ))}
              <button type="button" className="rounded border border-slate-300 bg-white px-3 py-2 text-sm font-semibold text-slate-700 hover:bg-slate-50" onClick={loadInsurers}>
                보험사 새로고침
              </button>
              <button type="button" className="rounded border border-rose-200 bg-white px-3 py-2 text-sm font-semibold text-rose-700 hover:bg-rose-50 disabled:opacity-50" disabled={loading || attachmentUploading || cleaningFiles || cleanupFileIds.length === 0} onClick={cleanupTestFiles}>
                {cleaningFiles ? '정리 중...' : '테스트 파일 정리'}
              </button>
            </div>
            {error && <div className="mt-4 rounded border border-rose-200 bg-rose-50 px-3 py-2 text-sm text-rose-700">{error}</div>}
            {cleanupMessage && <div className="mt-3 rounded border border-emerald-200 bg-emerald-50 px-3 py-2 text-sm text-emerald-700">{cleanupMessage}</div>}
          </div>

          <div className="rounded-lg border border-slate-200 bg-slate-50 p-4">
            <div className="flex flex-wrap items-center justify-between gap-2">
              <div>
                <div className="text-sm font-semibold text-slate-900">테스트 보험사</div>
                <div className="mt-0.5 text-xs text-slate-500">{selectedIds.length}개 선택 · 활성 서식 {insurers.length}개</div>
              </div>
              <div className="flex gap-2">
                <button type="button" className="rounded border border-slate-200 bg-white px-2.5 py-1.5 text-xs text-slate-700 hover:bg-slate-50" onClick={selectAll}>전체 선택</button>
                <button type="button" className="rounded border border-slate-200 bg-white px-2.5 py-1.5 text-xs text-slate-700 hover:bg-slate-50" onClick={clearSelected}>해제</button>
              </div>
            </div>
            <div className="mt-3 max-h-[360px] overflow-auto rounded border border-slate-200 bg-white">
              {insurers.length === 0 ? (
                <div className="px-4 py-8 text-center text-sm text-slate-500">활성 청구서식이 있는 보험사가 없습니다.</div>
              ) : insurers.map(insurer => {
                const selectable = isClaimTestInsurerSelectable(insurer)
                const checked = selectable && selectedSet.has(String(insurer.insurerOrgId))
                return (
                  <label key={insurer.insurerOrgId} className={`flex items-center gap-3 border-b border-slate-100 px-3 py-2 last:border-b-0 ${selectable ? 'cursor-pointer hover:bg-slate-50' : 'cursor-not-allowed bg-slate-50 opacity-70'}`}>
                    <input type="checkbox" checked={checked} disabled={!selectable} onChange={() => toggleInsurer(insurer.insurerOrgId)} />
                    <span className="min-w-0 flex-1">
                      <span className="block truncate text-sm font-medium text-slate-900">{insurer.insurerName}</span>
                      <span className="mt-0.5 flex flex-wrap gap-1.5 text-[11px] text-slate-500">
                        <span>서식 {insurer.formVersion || '-'}</span>
                        <span>예상 {insurer.expectedPageCount || '-'}p</span>
                        <span>추가항목 {insurer.additionalFieldCount || 0}</span>
                        {!insurer.claimEnabled && <span className="text-amber-700">청구 비활성</span>}
                        {insurer.templatePdfReady === false && <span className="text-rose-700">서식 PDF 누락</span>}
                      </span>
                    </span>
                  </label>
                )
              })}
            </div>
          </div>
        </div>
      </section>

      {inputMode === 'MANUAL' && (
        <section className="rounded-lg border border-slate-200 bg-white p-5 shadow-sm">
          <div className="flex flex-wrap items-center justify-between gap-3">
            <div>
              <h2 className="text-base font-semibold text-slate-900">직접 입력 필드</h2>
              <div className="mt-0.5 text-xs text-slate-500">{fieldDrafts.length}개 필드 · 선택 보험사 기준</div>
            </div>
            <div className="flex flex-wrap gap-2">
              <input
                className="w-48 rounded border border-slate-200 px-3 py-1.5 text-sm"
                value={manualFilter}
                onChange={e => setManualFilter(e.target.value)}
                placeholder="필드 검색"
              />
              <button type="button" className="rounded border border-slate-200 bg-white px-2.5 py-1.5 text-xs text-slate-700 hover:bg-slate-50" onClick={loadFieldDrafts}>필드 새로고침</button>
              <button type="button" className="rounded border border-slate-200 bg-white px-2.5 py-1.5 text-xs text-slate-700 hover:bg-slate-50" onClick={resetManualSamples}>샘플값 채우기</button>
              <button type="button" className="rounded border border-slate-200 bg-white px-2.5 py-1.5 text-xs text-slate-700 hover:bg-slate-50" onClick={clearManualValues}>비우기</button>
            </div>
          </div>
          {fieldDraftError && <div className="mt-3 rounded border border-rose-200 bg-rose-50 px-3 py-2 text-sm text-rose-700">{fieldDraftError}</div>}
          {selectedIds.length === 0 ? (
            <div className="mt-4 rounded border border-dashed border-slate-200 bg-slate-50 px-4 py-8 text-center text-sm text-slate-500">보험사를 선택하면 직접 입력 필드가 표시됩니다.</div>
          ) : fieldDraftLoading ? (
            <div className="mt-4 rounded border border-slate-200 bg-slate-50 px-4 py-8 text-center text-sm text-slate-500">입력 필드를 불러오는 중입니다.</div>
          ) : (
            <div className="mt-4 max-h-[560px] overflow-auto rounded border border-slate-200 bg-slate-50 p-4">
              {groupedManualFields.length === 0 ? (
                <div className="px-4 py-8 text-center text-sm text-slate-500">표시할 필드가 없습니다.</div>
              ) : groupedManualFields.map(group => (
                <div key={group.group} className="mb-5 last:mb-0">
                  <div className="mb-2 text-xs font-semibold text-slate-600">{group.group}</div>
                  <div className="grid gap-3 md:grid-cols-2 xl:grid-cols-3">
                    {group.fields.map(field => (
                      <label key={field.key} className="block rounded border border-slate-200 bg-white p-3 text-sm">
                        <span className="flex items-start justify-between gap-2">
                          <span className="min-w-0">
                            <span className="block truncate text-xs font-semibold text-slate-700">{field.label || field.key}</span>
                            <span className="mt-0.5 block truncate font-mono text-[11px] text-slate-400">{field.key}</span>
                          </span>
                          <span className="flex shrink-0 gap-1">
                            {field.required && <span className="rounded bg-rose-50 px-1.5 py-0.5 text-[10px] font-semibold text-rose-700">필수</span>}
                            {field.sensitive && <span className="rounded bg-amber-50 px-1.5 py-0.5 text-[10px] font-semibold text-amber-700">민감</span>}
                          </span>
                        </span>
                        <ClaimTestFieldInput field={field} value={manualValues[field.key]} onChange={value => updateManualValue(field.key, value)} />
                      </label>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          )}
        </section>
      )}

      {result && (
        <section className="rounded-lg border border-slate-200 bg-white p-5 shadow-sm">
          <div className="flex flex-wrap items-center justify-between gap-2">
            <div>
              <h2 className="text-base font-semibold text-slate-900">테스트 결과</h2>
              <div className="mt-0.5 text-xs text-slate-500">성공 {successCount}건 · 실패 {failedCount}건</div>
            </div>
            <span className="rounded-full bg-slate-100 px-3 py-1 text-xs font-semibold text-slate-700">{modeLabel(result.mode)}</span>
          </div>
          <div className="mt-4 overflow-x-auto">
            <table className="min-w-full divide-y divide-slate-200 text-sm">
              <thead className="bg-slate-50 text-xs font-semibold text-slate-600">
                <tr>
                  <th className="px-3 py-2 text-left">보험사</th>
                  <th className="px-3 py-2 text-left">상태</th>
                  <th className="px-3 py-2 text-left">첨부</th>
                  <th className="px-3 py-2 text-left">장수</th>
                  <th className="px-3 py-2 text-left">전체 크기</th>
                  <th className="px-3 py-2 text-left">장당 크기</th>
                  <th className="px-3 py-2 text-left">파일</th>
                  <th className="px-3 py-2 text-left">팩스</th>
                  <th className="px-3 py-2 text-left">접수번호</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {(asArray(result?.results)).map((row, idx) => (
                  <tr key={`${row.insurerOrgId || idx}-${row.fileId || idx}`}>
                    <td className="px-3 py-2 font-medium text-slate-900">{row.insurerName || `보험사 #${row.insurerOrgId || '-'}`}</td>
                    <td className="px-3 py-2">
                      <span className={`rounded-full px-2.5 py-1 text-xs font-semibold ${row.ok ? 'bg-emerald-50 text-emerald-700' : 'bg-rose-50 text-rose-700'}`}>
                        {row.ok ? row.message : row.message || '실패'}
                      </span>
                    </td>
                    <td className="px-3 py-2 text-slate-700">{Number(row.attachmentCount || 0) > 0 ? String(row.attachmentCount) + '개' : '-'}</td>
                    <td className="px-3 py-2 text-slate-700">{Number(row.generatedPageCount || 0) > 0 ? `${row.generatedPageCount}장` : '-'}</td>
                    <td className="px-3 py-2 text-slate-700">{Number(row.fileByteSize || row.pdfByteSize || 0) > 0 ? formatBytes(row.fileByteSize || row.pdfByteSize) : '-'}</td>
                    <td className="px-3 py-2 text-slate-700">{claimTestAverageBytesPerPage(row) > 0 ? formatBytes(claimTestAverageBytesPerPage(row)) : '-'}</td>
                    <td className="px-3 py-2">
                      {row.fileId ? (
                        <button
                          type="button"
                          className="rounded border border-slate-300 bg-white px-2.5 py-1.5 text-xs font-semibold text-slate-700 hover:bg-slate-50 disabled:opacity-50"
                          disabled={downloading === row.fileId}
                          onClick={() => downloadPdf(row)}
                        >
                          {downloading === row.fileId ? '다운로드 중...' : `${claimTestFileKind(row)} 다운로드`}
                        </button>
                      ) : '-'}
                    </td>
                    <td className="px-3 py-2 text-slate-700">{row.faxTransmissionId ? `#${row.faxTransmissionId} · ${statusLabel(row.faxStatus)}` : '-'}</td>
                    <td className="px-3 py-2 font-mono text-xs text-slate-500">{row.receiptNum || row.requestNum || '-'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </section>
      )}
    </div>
  )
}

export default function OpsApiTestPage({ initialMainTab = 'claims' }) {
  const targetYears = React.useMemo(() => latestThreeTaxYears(), [])
  const initialRange = React.useMemo(() => defaultDateRange(), [])
  const [mainTab, setMainTab] = React.useState(initialMainTab)
  const [tab, setTab] = React.useState('external')
  const [externalApiKey, setExternalApiKey] = React.useState('incomeTaxCredit')
  const [form, setForm] = React.useState(() => ({
    userName: '',
    identity: '',
    phoneNo: '',
    telecom: '0',
    loginType: '6',
    loginTypeLevel: '1',
    id: makeSessionId(),
    inquiryTypeCD: '001100000000000',
    authProvider: 'KAKAO',
    startDate: initialRange.startDate,
    endDate: initialRange.endDate,
    simpleAuth: '1',
  }))
  const [activeYear, setActiveYear] = React.useState(() => targetYears[0])
  const [yearResults, setYearResults] = React.useState({})
  const [twoWayInfoText, setTwoWayInfoText] = React.useState('')
  const [loading, setLoading] = React.useState('')
  const [error, setError] = React.useState('')
  const [result, setResult] = React.useState(null)
  const [resultMode, setResultMode] = React.useState('raw')
  const [copied, setCopied] = React.useState('')
  const [batchMessage, setBatchMessage] = React.useState('')
  const scenario = EXTERNAL_API_SCENARIOS[externalApiKey] || EXTERNAL_API_SCENARIOS.incomeTaxCredit

  const update = (key, value) => {
    setForm(prev => ({ ...prev, [key]: value }))
  }

  const buildPayload = (year = activeYear) => ({
    ...form,
    ...(scenario.multiYear ? { searchStartYear: year } : {}),
    identity: String(form.identity || '').replace(/[^0-9]/g, ''),
    phoneNo: String(form.phoneNo || '').replace(/[^0-9]/g, ''),
    startDate: String(form.startDate || '').replace(/[^0-9]/g, ''),
    endDate: String(form.endDate || '').replace(/[^0-9]/g, ''),
  })

  const applyYearResult = (year, data) => {
    setActiveYear(year)
    setResult(data)
    setYearResults(prev => ({
      ...prev,
      [year]: {
        code: codeOf(data),
        elapsedMs: data?.elapsedMs,
        result: data,
        updatedAt: new Date().toISOString(),
      },
    }))
    const nextTwoWayInfo = extractTwoWayInfo(data)
    if (nextTwoWayInfo) {
      setTwoWayInfoText(JSON.stringify(nextTwoWayInfo, null, 2))
    }
  }

  const markYearStatus = (year, code, message) => {
    setYearResults(prev => ({
      ...prev,
      [year]: {
        ...(prev[year] || {}),
        code,
        message,
        updatedAt: new Date().toISOString(),
      },
    }))
  }

  const requestInitialForYear = async (year) => {
    const res = await api.post(scenario.initialPath, buildPayload(year))
    return res?.data?.data || res?.data
  }

  const requestTwoWayForYear = async (year, twoWayInfo) => {
    const res = await api.post(scenario.twoWayPath, {
      ...buildPayload(year),
      simpleAuth: form.simpleAuth || '1',
      twoWayInfo,
    })
    return res?.data?.data || res?.data
  }

  const startPreflightForRemainingYears = (completedYear) => {
    const completedIdx = targetYears.indexOf(completedYear)
    const remaining = targetYears.slice(completedIdx + 1)
    const preflightPromises = {}
    for (const year of remaining) {
      markYearStatus(year, 'PREFLIGHT', '같은 id로 1차 요청 대기열 등록 중')
      preflightPromises[year] = requestInitialForYear(year)
        .then(data => ({ ok: true, data }))
        .catch(error => ({ ok: false, error }))
    }
    if (remaining.length > 0) {
      setBatchMessage(`${completedYear}년 추가 인증 대기 중입니다. ${remaining.join(', ')}년 1차 요청을 같은 다건 요청 ID로 미리 보냈습니다.`)
    }
    return preflightPromises
  }

  const callInitial = async (year = activeYear) => {
    setLoading(`initial-${year}`)
    setError('')
    setBatchMessage('')
    try {
      const data = await requestInitialForYear(year)
      applyYearResult(year, data)
    } catch (e) {
      setError(getApiErrorMessage(e, `${year}년 CODEF 1차 호출에 실패했습니다.`))
    } finally {
      setLoading('')
    }
  }

  const callLatestThreeYears = async () => {
    setLoading('batch')
    setError('')
    setBatchMessage('')
    setTwoWayInfoText('')
    setResult(null)
    setYearResults({})
    try {
      if (!scenario.multiYear) {
        const year = activeYear
        const data = await requestInitialForYear(year)
        applyYearResult(year, data)
        if (needsTwoWay(data)) {
          const nextTwoWayInfo = extractTwoWayInfo(data)
          setBatchMessage(`${scenario.label} 추가 인증이 필요합니다. 휴대폰 인증 완료 여부를 자동으로 확인합니다.`)
          if (nextTwoWayInfo) {
            await pollTwoWayAndContinue(year, nextTwoWayInfo)
          }
        } else if (codeOf(data) === 'CF-00000') {
          setBatchMessage(`${scenario.label} 조회를 완료했습니다.`)
        }
        return
      }
      for (const year of targetYears) {
        const data = await requestInitialForYear(year)
        applyYearResult(year, data)
        if (needsTwoWay(data)) {
          const nextTwoWayInfo = extractTwoWayInfo(data)
          const preflightPromises = startPreflightForRemainingYears(year)
          setBatchMessage(`${year}년 1차 호출에서 추가 인증이 필요합니다. 나머지 연도 1차 요청은 같은 id로 미리 보냈고, 휴대폰 인증 완료 여부를 자동 확인합니다.`)
          if (nextTwoWayInfo) {
            await pollTwoWayAndContinue(year, nextTwoWayInfo, preflightPromises)
          }
          break
        }
      }
    } catch (e) {
      setError(getApiErrorMessage(e, '최근 3개년 CODEF 호출에 실패했습니다.'))
    } finally {
      setLoading('')
    }
  }

  const continueRemainingYears = async (completedYear, preflightPromises = {}) => {
    if (!scenario.multiYear) {
      setBatchMessage(`${scenario.label} 조회를 완료했습니다.`)
      return
    }
    const completedIdx = targetYears.indexOf(completedYear)
    const remaining = targetYears.slice(completedIdx + 1)
    for (const year of remaining) {
      markYearStatus(year, 'WAITING_RESULT', 'preflight 결과 회수 중')
      const preflight = preflightPromises[year] ? await preflightPromises[year] : null
      if (preflight && !preflight.ok) {
        markYearStatus(year, 'PREFLIGHT_ERROR', getApiErrorMessage(preflight.error, 'preflight 요청 실패'))
      }
      const data = preflight?.ok ? preflight.data : await requestInitialForYear(year)
      applyYearResult(year, data)
      if (needsTwoWay(data)) {
        const nextTwoWayInfo = extractTwoWayInfo(data)
        setBatchMessage(`${year}년 조회에서 추가 인증 응답이 다시 내려왔습니다. 휴대폰 인증 완료 여부를 자동으로 확인합니다.`)
        if (nextTwoWayInfo) {
          await pollTwoWayAndContinue(year, nextTwoWayInfo)
        }
        return
      }
    }
    setBatchMessage('최근 3개년 조회를 완료했습니다.')
  }

  const pollTwoWayAndContinue = async (year, twoWayInfo, preflightPromises = {}) => {
    setLoading('auth-poll')
    for (let attempt = 1; attempt <= AUTO_AUTH_POLL_MAX_ATTEMPTS; attempt += 1) {
      setBatchMessage(`${year}년 간편인증 완료 확인 중... (${attempt}/${AUTO_AUTH_POLL_MAX_ATTEMPTS})`)
      await sleep(AUTO_AUTH_POLL_INTERVAL_MS)
      const data = await requestTwoWayForYear(year, twoWayInfo)
      applyYearResult(year, data)
      const code = codeOf(data)
      if (code === 'CF-00000') {
        setBatchMessage(`${year}년 인증 완료가 확인되었습니다. 남은 연도를 자동 조회합니다.`)
        await continueRemainingYears(year, preflightPromises)
        return true
      }
      if (code === 'CF-00025') {
        setBatchMessage(`${year}년 요청은 이미 완료된 상태입니다. 새 테스트가 필요하면 새 테스트 세션 ID로 다시 시작하세요.`)
        return false
      }
      if (code !== 'CF-03002') {
        setBatchMessage(`${year}년 인증 확인이 중단되었습니다. 응답 코드: ${code || 'UNKNOWN'}`)
        return false
      }
    }
    setBatchMessage(`${year}년 간편인증 확인 시간이 초과되었습니다. 새 테스트 세션 ID로 다시 시도해 주세요.`)
    return false
  }

  const callTwoWay = async () => {
    setLoading('twoway')
    setError('')
    setBatchMessage('')
    try {
      let twoWayInfo = null
      try {
        twoWayInfo = JSON.parse(twoWayInfoText)
      } catch {
        throw new Error('twoWayInfo JSON 형식이 올바르지 않습니다.')
      }
      const data = await requestTwoWayForYear(activeYear, twoWayInfo)
      applyYearResult(activeYear, data)
      if (codeOf(data) === 'CF-00000') {
        await continueRemainingYears(activeYear)
      }
    } catch (e) {
      setError(getApiErrorMessage(e, 'CODEF 2차 호출에 실패했습니다.'))
    } finally {
      setLoading('')
    }
  }

  const copyText = async (label, text) => {
    const value = text == null ? '' : String(text)
    if (!value.trim()) return
    try {
      if (navigator?.clipboard?.writeText) {
        await navigator.clipboard.writeText(value)
      } else {
        const el = document.createElement('textarea')
        el.value = value
        el.setAttribute('readonly', 'true')
        el.style.position = 'fixed'
        el.style.opacity = '0'
        document.body.appendChild(el)
        el.select()
        document.execCommand('copy')
        document.body.removeChild(el)
      }
      setCopied(label)
      setTimeout(() => setCopied(''), 1800)
    } catch (e) {
      setError(getApiErrorMessage(e, '클립보드 복사에 실패했습니다.'))
    }
  }

  const setMedicalOnly = () => update('inquiryTypeCD', '000100000000000')
  const setInsuranceAndMedical = () => update('inquiryTypeCD', '001100000000000')
  const recentYears = targetYears.join(', ')
  const hasAnyResult = scenario.multiYear
    ? targetYears.some(year => yearResults[year]?.result)
    : Boolean(result)
  const activeResult = scenario.multiYear ? (yearResults[activeYear]?.result || result) : result
  const combinedResult = scenario.multiYear
    ? {
        api: scenario.label,
        requestId: form.id,
        years: targetYears.reduce((acc, year) => {
          const row = yearResults[year]
          acc[year] = row?.result || {
            code: row?.code || 'WAITING',
            message: row?.message || '아직 호출 결과가 없습니다.',
          }
          return acc
        }, {}),
      }
    : (result ? (result.parsedBody || result.body || result) : null)
  const displayedResult = hasAnyResult ? pretty(combinedResult) : ''

  return (
    <div className="space-y-6">
      <div className="flex flex-col gap-3 sm:flex-row sm:items-end sm:justify-between">
        <div>
          <p className="text-xs font-semibold uppercase tracking-wide text-slate-500">운영관리 · 기타관리</p>
          <div className="mt-1 flex flex-wrap items-center gap-2">
            <h1 className="text-2xl font-semibold tracking-tight text-slate-900">운영테스트</h1>
            <MenuGuideButton slug="ops-management" title="운영관리 가이드" anchor="step-03" />
          </div>
        </div>
        {mainTab === 'api' && (
          <button
            type="button"
            className="w-full rounded border border-slate-200 bg-white px-3 py-2 text-sm font-medium text-slate-700 hover:bg-slate-50 sm:w-auto"
            onClick={() => update('id', makeSessionId())}
          >
            새 테스트 세션 ID
          </button>
        )}
      </div>

      <div className="flex gap-2 border-b border-slate-200">
        {[
          ['claims', '보험금청구'],
          ['coverage-analysis', '보장분석'],
          ['coverage-summary', '보장요약'],
          ['fhir', 'FHIR 테스트'],
          ['api', 'API 테스트'],
          ['notifications', '\uC54C\uB9BC\uD1A1 \uD14C\uC2A4\uD2B8'],
        ].map(([id, label]) => (
          <button
            key={id}
            type="button"
            className={`border-b-2 px-1 pb-2 text-sm font-semibold ${
              mainTab === id ? 'border-brand-navy text-brand-navy' : 'border-transparent text-slate-500 hover:text-slate-800'
            }`}
            onClick={() => setMainTab(id)}
          >
            {label}
          </button>
        ))}
      </div>

      {mainTab === 'claims' ? (
        <ClaimOperationTestPanel />
      ) : mainTab === 'coverage-analysis' ? (
        <CoverageOperationTestPanel mode="analysis" />
      ) : mainTab === 'coverage-summary' ? (
        <CoverageOperationTestPanel mode="summary" />
      ) : mainTab === 'fhir' ? (
        <FhirOperationTestPanel />
      ) : mainTab === 'notifications' ? (
        <NotificationOperationTestPanel />
      ) : (
        <>
      <div className="flex gap-2 border-b border-slate-200">
        {[
          ['internal', '내부 API'],
          ['external', '외부 API'],
        ].map(([id, label]) => (
          <button
            key={id}
            type="button"
            className={`border-b-2 px-1 pb-2 text-sm font-semibold ${
              tab === id ? 'border-brand-navy text-brand-navy' : 'border-transparent text-slate-500 hover:text-slate-800'
            }`}
            onClick={() => setTab(id)}
          >
            {label}
          </button>
        ))}
      </div>

      {tab === 'internal' ? (
        <section className="rounded-xl border border-dashed border-slate-200 bg-slate-50 px-5 py-10 text-center">
          <h2 className="text-base font-semibold text-slate-900">내부 API 테스트</h2>
          <p className="mt-2 text-sm text-slate-600">내부 운영 API 점검 도구를 이 탭에 순차적으로 추가할 예정입니다.</p>
        </section>
      ) : (
        <section className="grid gap-5 lg:grid-cols-[minmax(0,0.95fr)_minmax(0,1.05fr)]">
          <div className="rounded-xl border border-slate-200 bg-white p-5 shadow-sm">
            <div className="flex items-start justify-between gap-3">
              <div>
                <h2 className="text-base font-semibold text-slate-900">외부 API 테스트 시나리오</h2>
                <p className="mt-1 text-xs text-slate-500">
                  {scenario.description}
                </p>
              </div>
              <span className="rounded-full bg-amber-50 px-2.5 py-1 text-xs font-semibold text-amber-700">{scenario.badge}</span>
            </div>

            <label className="mt-4 block text-sm">
              <span className="text-xs font-medium text-slate-600">테스트 대상 API</span>
              <select
                className="mt-1 w-full rounded border px-3 py-2 text-sm"
                value={externalApiKey}
                onChange={e => {
                  setExternalApiKey(e.target.value)
                  setResult(null)
                  setYearResults({})
                  setTwoWayInfoText('')
                  setBatchMessage('')
                  setError('')
                }}
              >
                {Object.entries(EXTERNAL_API_SCENARIOS).map(([key, item]) => (
                  <option key={key} value={key}>{item.label}</option>
                ))}
              </select>
            </label>

            <div className="mt-5 grid gap-3 sm:grid-cols-2">
              <label className="block text-sm">
                <span className="text-xs font-medium text-slate-600">성명</span>
                <input className="mt-1 w-full rounded border px-3 py-2 text-sm" value={form.userName} onChange={e => update('userName', e.target.value)} />
              </label>
              <label className="block text-sm">
                <span className="text-xs font-medium text-slate-600">주민등록번호 13자리</span>
                <input className="mt-1 w-full rounded border px-3 py-2 text-sm" type="password" value={form.identity} onChange={e => update('identity', e.target.value)} placeholder="숫자만 입력" />
              </label>
              <label className="block text-sm">
                <span className="text-xs font-medium text-slate-600">휴대폰번호</span>
                <input className="mt-1 w-full rounded border px-3 py-2 text-sm" value={form.phoneNo} onChange={e => update('phoneNo', e.target.value)} placeholder="01012345678" />
              </label>
              <label className="block text-sm">
                <span className="text-xs font-medium text-slate-600">통신사</span>
                <select className="mt-1 w-full rounded border px-3 py-2 text-sm" value={form.telecom} onChange={e => update('telecom', e.target.value)}>
                  <option value="0">SKT / SKT 알뜰폰</option>
                  <option value="1">KT / KT 알뜰폰</option>
                  <option value="2">LG U+ / LG U+ 알뜰폰</option>
                </select>
              </label>
              <div className="block text-sm">
                <span className="text-xs font-medium text-slate-600">{scenario.multiYear ? '조회 귀속년도' : '조회 기간'}</span>
                <div className="mt-1 rounded border bg-slate-50 px-3 py-2 text-sm text-slate-700">
                  {scenario.multiYear ? `최근 3개년 자동: ${recentYears}` : `${formatDate(form.startDate)} ~ ${formatDate(form.endDate)}`}
                </div>
              </div>
              <label className="block text-sm">
                <span className="text-xs font-medium text-slate-600">간편인증 수단</span>
                <select
                  className="mt-1 w-full rounded border px-3 py-2 text-sm"
                  value={form.loginTypeLevel}
                  onChange={e => {
                    const level = e.target.value
                    const authProviderByLevel = { '1': 'KAKAO', '5': 'PASS', '6': 'NAVER', '8': 'TOSS' }
                    update('loginTypeLevel', level)
                    update('authProvider', authProviderByLevel[level] || 'KAKAO')
                  }}
                >
                  <option value="1">카카오톡</option>
                  <option value="5">통신사 PASS</option>
                  <option value="6">네이버</option>
                  <option value="8">토스</option>
                </select>
              </label>
            </div>

            {!scenario.multiYear && (
              <div className="mt-3 grid gap-3 sm:grid-cols-2">
                <label className="block text-sm">
                  <span className="text-xs font-medium text-slate-600">조회 시작일</span>
                  <input className="mt-1 w-full rounded border px-3 py-2 text-sm" value={form.startDate} onChange={e => update('startDate', e.target.value)} placeholder="YYYYMMDD" />
                </label>
                <label className="block text-sm">
                  <span className="text-xs font-medium text-slate-600">조회 종료일</span>
                  <input className="mt-1 w-full rounded border px-3 py-2 text-sm" value={form.endDate} onChange={e => update('endDate', e.target.value)} placeholder="YYYYMMDD" />
                </label>
              </div>
            )}

            <label className="mt-3 block text-sm">
              <span className="text-xs font-medium text-slate-600">다건 요청 ID</span>
              <input className="mt-1 w-full rounded border px-3 py-2 font-mono text-xs" value={form.id} onChange={e => update('id', e.target.value)} />
            </label>

            <div className="mt-3">
              <div className="mb-1 text-xs font-medium text-slate-600">조회 구분코드</div>
              <div className="flex flex-wrap gap-2">
                <button type="button" className="rounded border px-2.5 py-1.5 text-xs hover:bg-slate-50 disabled:opacity-40" disabled={!scenario.multiYear} onClick={setMedicalOnly}>의료비만</button>
                <button type="button" className="rounded border px-2.5 py-1.5 text-xs hover:bg-slate-50 disabled:opacity-40" disabled={!scenario.multiYear} onClick={setInsuranceAndMedical}>보험료+의료비</button>
                <input className="min-w-[170px] flex-1 rounded border px-3 py-1.5 font-mono text-xs disabled:bg-slate-100" disabled={!scenario.multiYear} value={form.inquiryTypeCD} onChange={e => update('inquiryTypeCD', e.target.value)} />
              </div>
            </div>

            <div className="mt-5 flex flex-wrap gap-2">
              <button
                type="button"
                className="rounded bg-brand-navy px-4 py-2 text-sm font-semibold text-white hover:opacity-95 disabled:opacity-50"
                disabled={Boolean(loading)}
                onClick={callLatestThreeYears}
              >
                {loading === 'batch' || loading === 'auth-poll' ? '자동 진행 중...' : (scenario.multiYear ? '최근 3개년 API 호출' : '외부 API 테스트 호출')}
              </button>
              {scenario.multiYear && (
                <button
                  type="button"
                  className="rounded border border-slate-300 bg-white px-4 py-2 text-sm font-semibold text-slate-800 hover:bg-slate-50 disabled:opacity-50"
                  disabled={Boolean(loading)}
                  onClick={() => callInitial(activeYear)}
                >
                  {loading === `initial-${activeYear}` ? `${activeYear}년 1차 호출 중...` : `${activeYear}년 1차 재호출`}
                </button>
              )}
              <button
                type="button"
                className="rounded border border-slate-300 bg-white px-4 py-2 text-sm font-semibold text-slate-800 hover:bg-slate-50 disabled:opacity-50"
                disabled={Boolean(loading) || !twoWayInfoText.trim()}
                onClick={callTwoWay}
              >
                {loading === 'twoway' ? '2차 호출 중...' : '수동 2차 호출'}
              </button>
            </div>

            {error && <div className="mt-4 rounded border border-rose-200 bg-rose-50 px-3 py-2 text-sm text-rose-700">{error}</div>}
            {batchMessage && <div className="mt-4 rounded border border-sky-200 bg-sky-50 px-3 py-2 text-sm text-sky-800">{batchMessage}</div>}

            <div className="mt-5 rounded-lg border border-slate-200 bg-slate-50 px-3 py-3">
              <div className="mb-2 text-xs font-semibold text-slate-600">{scenario.multiYear ? '연도별 호출 상태' : '호출 상태'}</div>
              <div className="flex flex-wrap gap-2">
                {(scenario.multiYear ? targetYears : [activeYear]).map(year => {
                  const row = yearResults[year]
                  const code = row?.code || '대기'
                  return (
                    <button
                      key={year}
                      type="button"
                      className={`rounded border px-3 py-2 text-left text-xs ${
                        activeYear === year ? 'border-brand-navy bg-white text-brand-navy' : 'border-slate-200 bg-white text-slate-700 hover:bg-slate-50'
                      }`}
                      onClick={() => {
                        setActiveYear(year)
                        if (row?.result) setResult(row.result)
                      }}
                    >
                      <div className="font-semibold">{scenario.multiYear ? `${year}년` : scenario.label}</div>
                      <div className="mt-0.5 font-mono">{code}</div>
                    </button>
                  )
                })}
              </div>
            </div>

            <div className="mt-5 rounded-lg border border-amber-100 bg-amber-50 px-3 py-2 text-xs text-amber-800">
              {scenario.multiYear
                ? `최근 3개년 호출은 ${targetYears[0]}년부터 같은 다건 요청 ID로 순차 실행합니다.`
                : '선택한 외부 API를 같은 인증/2차 확인 흐름으로 테스트합니다.'}
              추가 인증이 필요한 경우 화면이 5초 간격으로 인증 완료 여부를 자동 확인합니다. 수동 2차 호출은 자동 확인이 지연될 때만 사용하세요.
              같은 twoWayInfo로 2차 호출을 다시 실행하면 CODEF가 CF-00025(이미 응답 완료)로 응답할 수 있습니다.
            </div>
          </div>

          <div className="space-y-4">
            <div className="rounded-xl border border-slate-200 bg-white p-5 shadow-sm">
              <div className="mb-2 flex items-center justify-between">
                <h3 className="text-sm font-semibold text-slate-900">twoWayInfo</h3>
                <div className="flex items-center gap-2">
                  <span className="text-xs text-slate-500">CF-03002 응답 시 자동 반영</span>
                  <button
                    type="button"
                    className="rounded border border-slate-200 px-2 py-1 text-xs text-slate-700 hover:bg-slate-50 disabled:opacity-40"
                    disabled={!twoWayInfoText.trim()}
                    onClick={() => copyText('twoWayInfo', twoWayInfoText)}
                  >
                    {copied === 'twoWayInfo' ? '복사됨' : '복사'}
                  </button>
                </div>
              </div>
              <textarea
                className="min-h-[150px] w-full rounded border bg-slate-950 p-3 font-mono text-xs text-slate-100"
                value={twoWayInfoText}
                onChange={e => setTwoWayInfoText(e.target.value)}
                placeholder="1차 호출 결과의 data.jobIndex/threadIndex/jti/twoWayTimestamp 등이 들어갑니다."
              />
            </div>

            <div className="rounded-xl border border-slate-200 bg-white p-5 shadow-sm">
              <div className="mb-2 flex items-center justify-between">
                <h3 className="text-sm font-semibold text-slate-900">호출 결과</h3>
                <div className="flex items-center gap-2">
                  {activeResult?.elapsedMs != null && <span className="text-xs text-slate-500">{activeResult.elapsedMs}ms</span>}
                  <button
                    type="button"
                    className="rounded border border-slate-200 px-2 py-1 text-xs text-slate-700 hover:bg-slate-50 disabled:opacity-40"
                    disabled={!hasAnyResult}
                    onClick={() => setResultMode(m => (m === 'readable' ? 'raw' : 'readable'))}
                  >
                    {resultMode === 'readable' ? 'JSON 보기' : '결과 보기'}
                  </button>
                  <button
                    type="button"
                    className="rounded border border-slate-200 px-2 py-1 text-xs text-slate-700 hover:bg-slate-50 disabled:opacity-40"
                    disabled={!displayedResult}
                    onClick={() => copyText('result', displayedResult)}
                  >
                    {copied === 'result' ? '복사됨' : '결과 복사'}
                  </button>
                  <button
                    type="button"
                    className="rounded border border-slate-200 px-2 py-1 text-xs text-slate-700 hover:bg-slate-50 disabled:opacity-40"
                    disabled={!activeResult?.body}
                    onClick={() => copyText('rawBody', activeResult?.body)}
                  >
                    {copied === 'rawBody' ? '복사됨' : '원문 복사'}
                  </button>
                </div>
              </div>
              {resultMode === 'readable' ? (
                scenario.multiYear ? (
                  <MultiYearReadableResultView
                    years={targetYears}
                    yearResults={yearResults}
                    activeYear={activeYear}
                    scenario={scenario}
                    onSelectYear={(year) => {
                      setActiveYear(year)
                      if (yearResults[year]?.result) setResult(yearResults[year].result)
                    }}
                  />
                ) : (
                  <ReadableResultView result={result} year={activeYear} scenario={scenario} />
                )
              ) : (
                <pre className="max-h-[520px] overflow-auto rounded bg-slate-950 p-3 text-xs text-slate-100">
                  {hasAnyResult ? displayedResult : '아직 호출 결과가 없습니다.'}
                </pre>
              )}
            </div>
          </div>
        </section>
      )}
        </>
      )}
    </div>
  )
}
